import React, { useEffect, useState, useRef, useMemo } from "react";
import MapGL, { Marker, Popup, ScaleControl } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import L from "leaflet";
import Papa from "papaparse";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";
import { Source, Layer } from "react-map-gl/maplibre";
import { useLocation } from "react-router-dom";

import * as maplibregl from "maplibre-gl";
import { predictSmallVesselSync } from "./predictSmallVesselSync";

const API_BASE = "https://noisemaps.marble.eu";
// https://noisemaps.marble.eu
// http://localhost:3001

const buttonStyle = {
    backgroundColor: "#007bff",
    color: "white",
    border: "none",
    padding: "10px 20px",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "bold",
    fontSize: "14px",
    boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
    transition: "background-color 0.3s ease"
};



const getButtonStyle = (enabled) => ({
    ...buttonStyle,
    backgroundColor: enabled ? buttonStyle.backgroundColor : "#9e9e9e",
    color: enabled ? buttonStyle.color : "#ffffff",
    cursor: enabled ? "pointer" : "not-allowed",
    opacity: enabled ? 1 : 0.8,
    boxShadow: enabled ? buttonStyle.boxShadow : "none"
});


const buttonStyleSidebar = {
    backgroundColor: "#007bff",
    color: "white",
    border: "none",
    padding: "10px 20px",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "bold",
    fontSize: "18px",
    boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
    transition: "background-color 0.3s ease"
};
function getEstimatedDraught(ship) {
    const type = ship.AISTYPE;
    const length = getVesselLength(ship);
    let draught = parseFloat(ship.DRAUGHT);

    if (!isNaN(draught) && draught > 0) {
        return {
            draught: draught,
            label: `${draught.toFixed(1)} m`
        };
    }


    if (type === 36 || type === 37) {
        return {
            draught: 1,
            label: "N/A – assumed 1 m"
        };
    }

    if (type === 30) {
        return {
            draught: 3,
            label: "N/A – assumed 3 m"
        };
    }

    if (type >= 70 && type <= 79) {
        if (length < 100) {
            return { draught: 5, label: "N/A – assumed 5 m" };
        } else if (length < 150) {
            return { draught: 8, label: "N/A – assumed 8 m" };
        } else {
            return { draught: 10, label: "N/A – assumed 10 m" };
        }
    }

    if (type >= 80 && type <= 89) {
        if (length < 100) {
            return { draught: 5, label: "N/A – assumed 5 m" };
        } else if (length < 150) {
            return { draught: 8, label: "N/A – assumed 8 m" };
        } else {
            return { draught: 10, label: "N/A – assumed 10 m" };
        }
    }

    if (type >= 60 && type <= 69) {
        if (length < 50) {
            return { draught: 3, label: "N/A – assumed 3 m" };
        } else if (length < 100) {
            return { draught: 5, label: "N/A – assumed 5 m" };
        } else {
            return { draught: 8, label: "N/A – assumed 8 m" };
        }
    }

    return {
        draught: 5,
        label: "N/A – assumed 5 m"
    };
}

function getShipCategory(type) {
    if (type >= 60 && type <= 69) return "Passenger";
    if (type >= 70 && type <= 79) return "Cargo";
    if (type >= 80 && type <= 89) return "Tankers";
    if (type === 30) return "Fishing";
    if (type === 36) return "Sailing";
    if (type === 37) return "Pleasure";
    return "Unknown";
}
function isVesselSupported(ship, model) {
    const category = getShipCategory(ship.AISTYPE);

    const supported = {
        RANDI: ["Passenger", "Cargo", "Tankers"],
        JOMOPANS: ["Passenger", "Cargo", "Tankers", "Fishing"],
        LBDS: ["Passenger", "Cargo", "Tankers"],
        AQUO: ["Passenger", "Cargo", "Tankers", "Sailing", "Pleasure", "Fishing"],
        AVERAGE: ["Passenger", "Cargo", "Tankers", "Sailing", "Pleasure", "Fishing"],
        SMALL_VESSEL: ["Sailing", "Pleasure"],

    };

    return supported[model]?.includes(category) ?? false;
}


const db2lin = (L_dB) => Math.pow(10, L_dB / 10);
const lin2db = (P_lin) => 10 * Math.log10(Math.max(P_lin, 1e-20));

const K6 = Math.pow(2, 1 / 6);
const K12 = Math.pow(2, 1 / 12);

function thirdOctNodes(fc) {
    return [fc / K6, fc / K12, fc, fc * K12, fc * K6];
}

function booleWeightsLogF(fa, fb) {
    const h = (Math.log(fb) - Math.log(fa)) / 4;
    const base = [7, 32, 12, 32, 7];
    return base.map((c) => c * (2 * h) / 45);
}

function thirdOctBandLevel_fromPSD(psdFive_dB, fc) {
    const fNodes = thirdOctNodes(fc);
    const weights = booleWeightsLogF(fNodes[0], fNodes[4]);

    let sum = 0;
    for (let i = 0; i < 5; i++) {
        sum += weights[i] * db2lin(psdFive_dB[i]);
    }
    return lin2db(sum);
}


function broadband20_2000_viaThirdOct(psdFunc) {
    const centers = [
        20, 25, 31.5, 40, 50, 63, 80, 100, 125, 160,
        200, 250, 315, 400, 500, 630, 800, 1000, 1250,
        1600, 2000
    ];

    let Psum = 0;
    centers.forEach((fc) => {
        const fNodes = thirdOctNodes(fc);
        const psd5 = fNodes.map((f) => psdFunc(f));
        const bandLevel = thirdOctBandLevel_fromPSD(psd5, fc);
        Psum += db2lin(bandLevel);
    });

    return lin2db(Psum);
}



const getShipDescription = (type) => {
    if (type >= 60 && type <= 69) return "Passenger vessel";
    if (type >= 70 && type <= 79) return "Cargo vessel";
    if (type >= 80 && type <= 89) return "Tanker";
    if (type === 30) return "Fishing vessel";
    if (type === 36) return "Sailing vessel";
    if (type === 37) return "Pleasure craft";
    return "Unknown vessel type";
};

const knotsToMps = (knots) => knots / 1.94384;

function getVesselLength(ship) {
    const A = Number(ship.A) || 0;
    const B = Number(ship.B) || 0;
    const ab = A + B;

    const loa =
        Number(ship.LENGTH) ||
        Number(ship.length) ||
        Number(ship.LOA) ||
        Number(ship.loa) || 0;

    const L = loa > 0 ? loa : ab;
    return Math.max(L, 10);
}



function randi_psd_perHz(f, v_knots, length_m) {
    const logf = Math.log10(f);
    const length_ft = length_m * 3.28084;

    let SL0;
    if (f <= 500) {
        const t1 = Math.pow(10, (-1.06 * logf - 14.34));
        const t2 = Math.pow(10, (3.32 * logf - 21.425));
        SL0 = -10 * Math.log10(t1 + t2);
    } else {
        SL0 = 173.2 - 18 * logf;
    }

    const df = (f <= 28.4) ? 8.1 : (22.3 - 9.77 * logf);

    const dL = Math.pow(length_ft, 1.15) / 3643.0;

    const SL = SL0
        + 60 * Math.log10(v_knots / 12)
        + 20 * Math.log10(length_ft / 300)
        + df * dL
        + 3;

    return SL;
}


function booleBandLevel_log(fc, v_knots, length_m) {
    const k = Math.pow(2, 1 / 6);
    let f_lo = fc / k;
    let f_hi = fc * k;

    const log_lo = Math.log(f_lo);
    const log_hi = Math.log(f_hi);

    const h = (log_hi - log_lo) / 4;

    const w = [7, 32, 12, 32, 7];
    let sum = 0;

    for (let i = 0; i < 5; i++) {
        const log_fi = log_lo + h * i;
        const fi = Math.exp(log_fi);

        const SL_psd = randi_psd_perHz(fi, v_knots, length_m);
        const p_lin = Math.pow(10, SL_psd / 10);

        sum += w[i] * p_lin * fi;
    }

    const integral = (2 * h / 45) * sum;
    return 10 * Math.log10(integral);
}

function broadbandRANDI(v_knots, length_m) {
    const centers = [
        20, 25, 31.5, 40, 50, 63, 80, 100,
        125, 160, 200, 250, 315, 400, 500,
        630, 800, 1000, 1250, 1600, 2000
    ];

    let E = 0;

    for (const fc of centers) {
        const Lb = booleBandLevel_log(fc, v_knots, length_m);
        E += Math.pow(10, Lb / 10);
    }

    return 10 * Math.log10(E);
}

function normalizeClass(c) {
    c = c.toLowerCase();
    if (c.includes("tanker")) return "tanker";
    if (c.includes("cargo") || c.includes("container") || c.includes("bulker")) return "cargo";
    if (c.includes("passenger")) return "passenger";
    if (c.includes("fishing")) return "fishing";
    return "other";
}

function lsSpectralDensity_JECHO(f, vesselClass, speedKnots, lengthM) {
    const L0 = 91.44;
    const K = 191.0;
    const K_LF = 208.0;

    const VC = {
        fishing: 6.4,
        cargo: 13.9,
        tanker: 12.4,
        passenger: 9.7,
        other: 7.4
    };

    const DLF = { cargo: 0.8, tanker: 1.0 };

    vesselClass = normalizeClass(vesselClass);
    const vc = VC[vesselClass] || 7.4;

    const fhat = f / 1.0;
    const fhat1 = 480 * (1 / vc);
    const fhat1_LF = 600 * (1 / vc);

    let LS0;

    if ((vesselClass === "cargo" || vesselClass === "tanker") && f < 100) {
        const dlf = DLF[vesselClass];
        const inner = 1 - (fhat / fhat1) ** 2;
        const denom = inner ** 2 + dlf ** 2;
        LS0 = K_LF - 40 * Math.log10(fhat1_LF) + 10 * Math.log10(fhat) - 10 * Math.log10(denom);
    } else {
        const D = 3.0;
        const inner = 1 - (fhat / fhat1);
        const denom = inner ** 2 + D ** 2;
        LS0 = K - 20 * Math.log10(fhat1) - 10 * Math.log10(denom);
    }

    const speedTerm = 60 * Math.log10(speedKnots / vc);
    const lengthTerm = 20 * Math.log10(lengthM / L0);

    return LS0 + speedTerm + lengthTerm;
}

function booleIntegrate(func, a, b) {
    const h = (b - a) / 4;
    const f0 = func(a);
    const f1 = func(a + h);
    const f2 = func(a + 2 * h);
    const f3 = func(a + 3 * h);
    const f4 = func(b);

    return (2 * h / 45) * (7 * f0 + 32 * f1 + 12 * f2 + 32 * f3 + 7 * f4);
}

function thirdOctBandEdges(fc) {
    const ratio = Math.pow(10, 0.05);
    return [fc / ratio, fc * ratio];
}

function jomopansBand(fc, ship) {
    const [f1, f2] = thirdOctBandEdges(fc);

    const psd = (f) => {
        const L = lsSpectralDensity_JECHO(
            f,
            getShipCategory(ship.AISTYPE),
            Math.max(ship.SPEED, 0.1),
            getVesselLength(ship)
        );
        return Math.pow(10, L / 10);
    };

    const power = booleIntegrate(psd, f1, f2);
    return 10 * Math.log10(power);
}


function jomopansBroadband(ship) {
    const centers = [
        20, 25, 31.5, 40, 50, 63, 80, 100, 125, 160,
        200, 250, 315, 400, 500, 630, 800, 1000, 1250,
        1600, 2000
    ];

    let sum = 0;
    for (const fc of centers) {
        const L = jomopansBand(fc, ship);
        sum += Math.pow(10, L / 10);
    }
    return 10 * Math.log10(sum);
}


function log10(x) {
    return Math.log(x) / Math.LN10;
}

function lin(x) {
    return Math.pow(10, x / 10);
}

function dB(x) {
    return 10 * Math.log10(x);
}


function thirdOctNodes2(fc) {
    const ratio = Math.pow(2, 1 / 6);
    const f1 = fc / ratio;
    const f2 = fc * ratio;

    const h = (Math.log(f2) - Math.log(f1)) / 4;

    return [
        Math.exp(Math.log(f1)),
        Math.exp(Math.log(f1) + h),
        Math.exp(Math.log(f1) + 2 * h),
        Math.exp(Math.log(f1) + 3 * h),
        Math.exp(Math.log(f2))
    ];
}



function booleWeights() {
    return [7, 32, 12, 32, 7];
}

function getAQUOparams(ship) {
    const cat = getShipCategory(ship.AISTYPE);

    if (cat === "Cargo" || cat === "Tankers") {
        return {
            Lref: 180,
            Vcav: 10
        };
    }

    if (cat === "Passenger") {
        return {
            Lref: 150,
            Vcav: 12
        };
    }

    if (cat === "Fishing") {
        return {
            Lref: 80,
            Vcav: 8
        };
    }

    return {
        Lref: 180,
        Vcav: 10
    };
}


function SL_mach(f, V) {
    if (f < 200) {
        return 136 + 15 * log10(V);
    }
    return 186 - 22 * log10(f) + 15 * log10(V);
}

function SL_prop(f, V) {
    if (f < 80) {
        return 109 - 51 * log10(f) + 50 * log10(V);
    }
    return 156 - 30 * log10(f) + 50 * log10(V);
}

function SL_cav(f, V) {
    if (V <= 10) return null;

    if (f < 50) {
        return 79 + 10 * log10(f) + 60 * log10(V);
    }
    return 129 - 20 * log10(f) + 60 * log10(V);
}

function SL_total(f, V, L, params) {
    const { Lref, Vcav } = params;

    const mach = SL_mach(f, V);
    const prop = SL_prop(f, V);

    let sum = lin(mach) + lin(prop);

    if (V > Vcav) {
        const cav = SL_cav(f, V);
        sum += lin(cav);
    }

    return dB(sum) + 25 * log10(L / Lref);
}


function AQUO_band(fc, V, L, ship) {
    const nodes = thirdOctNodes2(fc);
    const weights = booleWeights();
    const params = getAQUOparams(ship);

    let sum = 0;
    for (let i = 0; i < 5; i++) {
        const SL = SL_total(nodes[i], V, L, params);
        sum += weights[i] * lin(SL);
    }

    const df_log = (Math.log(nodes[4]) - Math.log(nodes[0])) / 4;
    return dB((2 * df_log / 45) * sum);
}



function AQUO_broadband(V, L, ship) {
    const centers = [
        20, 25, 31.5, 40, 50, 63, 80, 100, 125, 160,
        200, 250, 315, 400, 500, 630, 800, 1000, 1250,
        1600, 2000
    ];

    let sum = 0;
    for (const fc of centers) {
        const Lband = AQUO_band(fc, V, L, ship);
        sum += lin(Lband);
    }

    return dB(sum);
}


function AQUO_sailboat(band) {
    if (band === "63Hz") return 120;
    if (band === "125Hz") return 120;
    if (band === "Broadband") return 133;
    return 120;
}


function SRV_model(V, L) {
    const b0 = 100.3;
    const bL = 19.1 * log10(L);
    const bV = 27.8 * log10(V);
    const bLV = (-3.1) * log10(L * V);

    return b0 + bL + bV + bLV;
}




function calculateNoiseByModel(ship, model, band) {


    if (!isVesselSupported(ship, model)) {
        return null;
    }

    const length = getVesselLength(ship);
    const vKnots = ship.SPEED || 0;
    const v = Math.max(vKnots, 1);
    let sourceSpl = 0;


    if (model === "RANDI") {
        const vKnots = Math.max(ship.SPEED || 0, 0.1);

        if (band === "63Hz") {
            return booleBandLevel_log(63, vKnots, length);
        }
        if (band === "125Hz") {
            return booleBandLevel_log(125, vKnots, length);
        }
        if (band === "Broadband") {
            return broadbandRANDI(vKnots, length);
        }
    }


    else if (model === "JOMOPANS") {
        if (band === "63Hz") return jomopansBand(63, ship);
        if (band === "125Hz") return jomopansBand(125, ship);
        if (band === "Broadband") return jomopansBroadband(ship);
    }


    else if (model === "LBDS") {

        function SAS4_small(f) {
            const lf = Math.log10(f);
            const df = lf - 2.70016;
            return (
                196.26 +
                (-10.82) * lf +
                13.56 * (df ** 2) +
                (-6.27) * (df ** 3) +
                (-5.71) * (df ** 4) +
                3.78 * (df ** 5)
            );
        }

        function SAS4_large(f, speed) {
            const lf = Math.log10(f);
            const df = lf - 2.70016;
            return (
                174.96 +
                (-7.42) * lf +
                14.77 * (df ** 2) +
                (-14.54) * (df ** 3) +
                (-5.55) * (df ** 4) +
                6.58 * (df ** 5) +
                0.80 * speed
            );
        }

        const isLarge = (length > 250);
        const SPEED = ship.SPEED || 0;

        if (band === "63Hz") {
            sourceSpl = isLarge ? SAS4_large(63, SPEED) : SAS4_small(63);
        }

        else if (band === "125Hz") {
            sourceSpl = isLarge ? SAS4_large(125, SPEED) : SAS4_small(125);
        }

        else if (band === "Broadband") {
            const freqBands = [
                20, 25, 31.5, 40, 50, 63, 80, 100, 125, 160,
                200, 250, 315, 400, 500, 630, 800, 1000, 1250,
                1600, 2000
            ];

            let P = 0;
            for (const f of freqBands) {
                const SL = isLarge ? SAS4_large(f, SPEED) : SAS4_small(f);
                P += Math.pow(10, SL / 10);
            }
            sourceSpl = 10 * Math.log10(P);
        }

    }


    if (model === "AQUO") {

        const V = Math.max(ship.SPEED || 0.1, 0.1);
        const L = getVesselLength(ship);
        const type = ship.AISTYPE;

        if (type === 36) {
            return AQUO_sailboat(band);
        }

        if (band === "63Hz") return AQUO_band(63, V, L, ship);
        if (band === "125Hz") return AQUO_band(125, V, L, ship);
        if (band === "Broadband") return AQUO_broadband(V, L, ship);
    }


    else if (model === "SMALL_VESSEL") {
        const V = Math.max(ship.SPEED || 0.1, 0.1);
        const L = getVesselLength(ship);

        const vesselType =
            ship.AISTYPE === 36 ? "sailboat" :
                ship.AISTYPE === 37 ? "yacht" :
                    "yacht";

        const res = predictSmallVesselSync(vesselType, L, V);

        if (!res) return null;

        if (band === "63Hz") return res.L_63Hz_1_3;
        if (band === "125Hz") return res.L_125Hz_1_3;
        return res.L_20_2000Hz;
    }









    else if (model === "AVERAGE") {

        const models = ["RANDI", "JOMOPANS", "LBDS", "AQUO", "SMALL_VESSEL"];

        const weights = {
            RANDI: 1.0,
            JOMOPANS: 1.0,
            LBDS: 0.9,
            AQUO: 1.0,
            SMALL_VESSEL: 0.9
        };

        let Psum = 0;
        let wsum = 0;

        for (const m of models) {
            const LdB = calculateNoiseByModel(ship, m, band);

            if (!isFinite(LdB)) continue;

            const P = Math.pow(10, LdB / 10);  
            Psum += weights[m] * P;
            wsum += weights[m];
        }

        if (wsum > 0 && Psum > 0) {
            sourceSpl = 10 * Math.log10(Psum / wsum); 
        } else {
            sourceSpl = null;
        }
    }



    return sourceSpl;
}






const calculateNoise = (speedMps, length) => {
    const v = Math.max(Number(speedMps) || 0, 0.5);
    const L = Math.max(Number(length) || 0, 10);
    const SPL = 170 + 2 * Math.log10(L) + 5 * Math.log10(v);
    return SPL;
};



const ShipMap = () => {
    const [selectedShip, setSelectedShip] = useState(null);
    const navigate = useNavigate();
    const [shipData, setShipData] = useState([]);
    const [timeColumn, setTimeColumn] = useState("DATE TIME (UTC)");
    const [currentTime, setCurrentTime] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [playSpeed, setPlaySpeed] = useState(10000);
    const [activeShips, setActiveShips] = useState({});
    const [minTime, setMinTime] = useState(null);
    const [maxTime, setMaxTime] = useState(null);
    const [selectedMinDate, setSelectedMinDate] = useState(null);
    const [selectedMaxDate, setSelectedMaxDate] = useState(null);
    const timeThreshold = 30 * 60 * 1000;
    const [showNavstat0, setShowNavstat0] = useState(true);
    const [showNavstat1, setShowNavstat1] = useState(true);
    const [zoomLevel, setZoomLevel] = useState(4);
    const [showHeatmapLegend, setShowHeatmapLegend] = useState(true);
    const [showShipIconLegend, setShowShipIconLegend] = useState(true);
    const [mapKey, setMapKey] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const location = useLocation();
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [showHeatmap, setShowHeatmap] = useState(true);
    const [showIcons, setShowIcons] = useState(true);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [loginPassword, setLoginPassword] = useState("");
    const [isLoggedIn, setIsLoggedIn] = useState(() => !!sessionStorage.getItem("jwtToken"));
    const [loginError, setLoginError] = useState("");

    const [actualDepth, setActualDepth] = useState(null);
    const [isPickingLocation, setIsPickingLocation] = useState(false);

    const mapRef = useRef(null);
    const fetchController = useRef(null);

const [heatmapBoost, setHeatmapBoost] = useState(1);
const [depthError, setDepthError] = useState("");
const pickHintRef = useRef(null);
const [noiseMin, setNoiseMin] = useState(80);
const [noiseMax, setNoiseMax] = useState(190);

const legendTicks = useMemo(() => {
    const min = Number(noiseMin);
    const max = Number(noiseMax);

    if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
        return [];
    }

    const tickCount = 6;

    return Array.from({ length: tickCount }, (_, i) => {
        const value = min + (i * (max - min)) / (tickCount - 1);
        return Math.round(value);
    });
}, [noiseMin, noiseMax]);

    const [showCalcModal, setShowCalcModal] = useState(false);
    const [calcLat, setCalcLat] = useState("");
    const [calcLng, setCalcLng] = useState("");
    const [calcDepth, setCalcDepth] = useState("");
    const [calcResult, setCalcResult] = useState(null);
    const [activeTab, setActiveTab] = useState("controls1");
    const [shipDataRaw, setShipDataRaw] = useState([]);
    const [dataSource, setDataSource] = useState("api");
    const [selectedBand, setSelectedBand] = useState("63Hz");
    const [selectedModel, setSelectedModel] = useState("AVERAGE");
    const [availableMonth, setAvailableMonth] = useState(null);
    const [isDaysLoading, setIsDaysLoading] = useState(false);
    const [isSelectOpen, setIsSelectOpen] = useState(false);
    const [showShipModal, setShowShipModal] = useState(false);
    const [selectedDepth, setSelectedDepth] = useState(0);
    const depthSeaThreshold = -3;
    const [dynamicMinSL, setDynamicMinSL] = useState(80);
    const [showMPA, setShowMPA] = useState(false);
    const [mpaLoading, setMpaLoading] = useState(false);

    const [loginEmail, setLoginEmail] = useState("");
    const [authMode, setAuthMode] = useState("login");
    const [regSurname, setRegSurname] = useState("");
    const [regAffiliation, setRegAffiliation] = useState("");
    const [regIntendedUse, setRegIntendedUse] = useState("Academic use");
    const [showAdminPanel, setShowAdminPanel] = useState(false);
    const [adminUsers, setAdminUsers] = useState([]);
    const [adminError, setAdminError] = useState("");
    const [deleteUserId, setDeleteUserId] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [captchaToken, setCaptchaToken] = useState("");

    const [regName, setRegName] = useState("");
    const [regEmail, setRegEmail] = useState("");
    const [regPassword, setRegPassword] = useState("");
    const [regError, setRegError] = useState("");
    const [regPasswordConfirm, setRegPasswordConfirm] = useState("");
    const [openedFromAdmin, setOpenedFromAdmin] = useState(false);

    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
    const [showLoginPassword, setShowLoginPassword] = useState(false);
    const [showRegisterSuccess, setShowRegisterSuccess] = useState(false);
    const intensityMultiplier = selectedModel === "SMALL_VESSEL" ? 2.0 : 1.0;
    const [mapReady, setMapReady] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportDate, setExportDate] = useState(null);
    const [isExporting, setIsExporting] = useState(false);
    const [heatmapLoading, setHeatmapLoading] = useState(false);
    const isSRV = selectedModel === "SMALL_VESSEL";
    const [suppressHeatmapLoading, setSuppressHeatmapLoading] = useState(false);

    const SRV_RADIUS_BOOST = 3.0;     

    const openLogin = () => {
        setAuthMode("login");
        setLoginEmail("");
        setLoginPassword("");
        setLoginError("");
        setShowLoginModal(true);
    };

    const closeLoginModal = () => {
        setShowLoginModal(false);
        setAuthMode("login");
    };

    const HEATMAP_SOURCE_IDS = [
        "heatmap-jadran-sjever",
        "heatmap-jadran-jug",
        "ship-noise-heatmap-haifa-jug",
        "ship-noise-heatmap-haifa-sjever",
        "heatmap-uk",
        "heatmap-north"
    ];

    const hasData = shipData && shipData.length > 0 && currentTime !== null;


    const loadUsers = async () => {
        try {
            const res = await fetch(`${API_BASE}/api`);
            const data = await res.json();

            if (data.success) {
                setAdminUsers(data.users);
            } else {
                setAdminError("Failed to load users");
            }
        } catch (err) {
            setAdminError("Server error");
        }
    };


    const deleteUser = async (id) => {

        try {
            const res = await fetch(`${API_BASE}/api/delete-user`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id })
            });

            const data = await res.json();

            if (data.success) {
                setAdminUsers(prev => prev.filter(u => u.id !== id));
            } else {
                alert("Delete failed");
            }
        } catch (err) {
            alert("Server error");
        }
    };


    const getDynamicMinSourceLevel = () => {
        const movingShips = shipData.filter(ship =>
            typeof ship.SPEED === "number" && ship.SPEED >= 2
        );

        if (movingShips.length === 0) return noiseMin;

        let minSL = Infinity;

        for (const ship of movingShips) {
            const sl = calculateNoiseByModel(ship, selectedModel, selectedBand);

            if (sl === null || !isFinite(sl)) continue;

            minSL = Math.min(minSL, sl);
        }

        return isFinite(minSL) ? Math.round(minSL) : noiseMin;
    };




    function haversineDistance(lat1, lon1, lat2, lon2) {
        function toRad(x) { return x * Math.PI / 180; }
        const R = 6371000;
        const dLat = toRad(lat2 - lat1);
        const dLon = toRad(lon2 - lon1);
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }
    const handleCSVUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsLoading(true);

        const rows = [];
        let minTs = Infinity, maxTs = -Infinity;

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            worker: true,
            chunkSize: 1024 * 1024,
            step: (result) => {
                const r = result.data;
                const tsRaw = r.timestamp || r["DATE TIME (UTC)"];
                const ts = tsRaw
                    ? (tsRaw.includes("T") ? new Date(tsRaw) : new Date(String(tsRaw).replace(" ", "T") + "Z"))
                    : null;

                const row = {
                    MMSI: r.MMSI || r.mmsi,
                    LATITUDE: parseFloat(r.LATITUDE || r.latitude),
                    LONGITUDE: parseFloat(r.LONGITUDE || r.longitude),
                    SPEED: parseFloat(r.SPEED || r.speed),
                    NAVSTAT: parseInt(r.NAVSTAT || r.navstat),
                    NAME: r.NAME || r.name,
                    AISTYPE: parseInt(r.AISTYPE || r.type),
                    A: parseInt(r.A || 0),
                    B: parseInt(r.B || 0),
                    LENGTH: (parseFloat(r.a) || 0) + (parseFloat(r.b) || 0),
                    COURSE: parseFloat(r.COURSE || r.course),
                    HEADING: parseFloat(r.HEADING || r.heading),
                    timestamp: ts ? ts.toISOString() : null,
                    DRAUGHT: parseFloat(r.DRAUGHT || r.draught || 0),
                };


                if (!Number.isFinite(row.LATITUDE) || !Number.isFinite(row.LONGITUDE)) return;

                rows.push(row);

                if (row.timestamp) {
                    const t = new Date(row.timestamp).getTime();
                    if (Number.isFinite(t)) {
                        if (t < minTs) minTs = t;
                        if (t > maxTs) maxTs = t;
                    }
                }
            },
            complete: () => {
                setIsLoading(false);
                setDataSource("csv");
                setTimeColumn("timestamp");
                setShipDataRaw(rows);

                const extent = {
                    min: minTs === Infinity ? null : new Date(minTs),
                    max: maxTs === -Infinity ? null : new Date(maxTs),
                };
                setCsvDateExtent(extent);

                if (extent.min) {
                    const firstDay = new Date(extent.min);
                    firstDay.setHours(0, 0, 0, 0);
                    setSelectedMinDate(firstDay);
                    applyCsvDay(firstDay, rows);


                    const firstRowWithTime = rows.find(r => r.timestamp);
                    if (firstRowWithTime) {
                        setCurrentTime(new Date(firstRowWithTime.timestamp));
                    }
                    setMinTime(minTs);
                    setMaxTime(maxTs);
                    setMapKey(prev => prev + 1);
                }

            },
            error: () => {
                setIsLoading(false);
                alert("Error parsing CSV!");
            }
        });
    };

    function toUTCDateKey(date) {
        const y = date.getUTCFullYear();
        const m = String(date.getUTCMonth() + 1).padStart(2, "0");
        const d = String(date.getUTCDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    }
    function monthKeyFromDate(date) {
        const y = date.getUTCFullYear();
        const m = String(date.getUTCMonth() + 1).padStart(2, "0");
        return `${y}-${m}`;
    }

    const getRelevantVesselTypesForModel = (model) => {
        switch (model) {
            case "RANDI":
                return ["Passenger", "Cargo", "Tankers"];
            case "JOMOPANS":
                return ["Passenger", "Cargo", "Tankers", "Fishing"];
            case "LBDS":
                return ["Passenger", "Cargo", "Tankers"];
            case "AQUO":
            case "AVERAGE":
                return ["Passenger", "Cargo", "Tankers", "Sailing", "Pleasure", "Fishing"];
            case "SMALL_VESSEL":
                return ["Sailing", "Pleasure"];

            default:
                return [];
        }
    };

    const selectContainerStyle = {
        position: "absolute",
        bottom: 168,
        right: 10,
        backgroundColor: "white",
        padding: "10px",
        borderRadius: "5px",
        boxShadow: "0 2 5px rgba(0,0,0,0.3)",
        zIndex: 1000,
        fontSize: "14px",
        marginBottom: "170px"
    };

    const labelStyle = {
        fontWeight: "bold",
        fontSize: "14px",
        color: "black",
        letterSpacing: "0.2px",
    };

    const selectBoxStyle = {
        width: "100%",
        padding: "10px 12px",
        borderRadius: "8px",
        border: "1px solid #cbd5e1",
        background: "#fff",
        fontSize: "14px",
        color: "#222",
        boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
        outline: "none",
        transition: "border-color .2s ease, box-shadow .2s ease, background-color .2s ease",
        cursor: "pointer",
    };



    const fixedActiveDays = (() => {
        const set = new Set();

        const start1 = new Date(Date.UTC(2025, 5, 21));
        const end1 = new Date(Date.UTC(2025, 8, 19));
        for (let d = new Date(start1); d <= end1; d.setUTCDate(d.getUTCDate() + 1)) {
            const y = d.getUTCFullYear();
            const m = String(d.getUTCMonth() + 1).padStart(2, "0");
            const day = String(d.getUTCDate()).padStart(2, "0");
            set.add(`${y}-${m}-${day}`);
        }

        const start2 = new Date(Date.UTC(2025, 10, 7));
        const end2 = new Date(Date.UTC(2025, 11, 5));


        for (let d = new Date(start2); d <= end2; d.setUTCDate(d.getUTCDate() + 1)) {
            const y = d.getUTCFullYear();
            const m = String(d.getUTCMonth() + 1).padStart(2, "0");
            const day = String(d.getUTCDate()).padStart(2, "0");
            set.add(`${y}-${m}-${day}`);
        }
        const start3 = new Date(Date.UTC(2025, 11, 15));
        const today = new Date();

        const end3 = new Date(Date.UTC(
            today.getUTCFullYear(),
            today.getUTCMonth(),
            today.getUTCDate()
        ));

        for (let d = new Date(start3); d <= end3; d.setUTCDate(d.getUTCDate() + 1)) {
            const y = d.getUTCFullYear();
            const m = String(d.getUTCMonth() + 1).padStart(2, "0");
            const day = String(d.getUTCDate()).padStart(2, "0");
            set.add(`${y}-${m}-${day}`);
        }
        set.add("2025-11-05");
        set.delete("2025-11-14");
        set.delete("2025-11-15");
        set.delete("2025-11-16");
        set.delete("2025-11-17");


        return set;
    })();


    const [availableDaysSet, setAvailableDaysSet] = useState(new Set(fixedActiveDays));

     const handleCalculate = () => {
        const lat = parseFloat(calcLat);
        const lng = parseFloat(calcLng);

         const userDepth = parseFloat(calcDepth);
         const userDepthSigned = isNaN(userDepth) ? 0 : -Math.abs(userDepth);

         if (actualDepth !== null && userDepthSigned < actualDepth) {
             setDepthError(`Entered depth (${userDepthSigned} m) is shallower than the actual depth (${actualDepth.toFixed(2)} m)`);
             return;
         } else {
             setDepthError("");
         }

        let maxSpl = null;
        let maxShip = null;

        shipData.forEach(ship => {
            const horizontalDist = haversineDistance(
                lat,
                lng,
                ship.LATITUDE,
                ship.LONGITUDE
            );

            const sourceSpl = calculateNoiseByModel(
                ship,
                selectedModel,
                selectedBand
            );

            if (sourceSpl === null || !isFinite(sourceSpl)) return;

            const splAtPoint =
                sourceSpl - 20 * Math.log10(Math.max(horizontalDist, 1));

            if (maxSpl === null || splAtPoint > maxSpl) {
                maxSpl = splAtPoint;
                maxShip = ship;
            }
        });

        setCalcResult({
            spl: maxSpl+45,
            ship: maxShip
        });
    };


    function updateTimeBoundsFromRows(rows) {
        let minTs = Infinity, maxTs = -Infinity, firstTs = null;
        for (let i = 0; i < rows.length; i++) {
            const v = rows[i][timeColumn];
            if (!v) continue;
            const ts = new Date(v).getTime();
            if (!Number.isFinite(ts)) continue;
            if (firstTs === null || ts < firstTs) firstTs = ts;
            if (ts < minTs) minTs = ts;
            if (ts > maxTs) maxTs = ts;
        }
        if (minTs === Infinity) {
            setCurrentTime(null); setMinTime(null); setMaxTime(null);
        } else {
            const ct = new Date(firstTs ?? minTs);
            setCurrentTime(ct); setMinTime(minTs); setMaxTime(maxTs);
        }
    }
    function getAllRowsForSelectedDay() {
        if (!selectedMinDate) return [];

        if (dataSource === "csv") {
            return filterRowsForDay(shipDataRaw, selectedMinDate);
        } else {
            return filterRowsForDay(shipData, selectedMinDate);
        }
    }


    function filterRowsForDay(rows, date) {
        if (!date) return [];
        const dayStart = new Date(date);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(date);
        dayEnd.setHours(23, 59, 59, 999);

        return rows.filter(r => {
            const v = r?.[timeColumn] || r?.timestamp || r?.["DATE TIME (UTC)"];
            const t = v ? new Date(v) : null;
            return t && t >= dayStart && t <= dayEnd;
        });

    }

    function applyCsvDay(date, rows = shipDataRaw) {
        const filtered = filterRowsForDay(rows, date);
        setShipData(filtered);
        updateTimeBoundsFromRows(filtered);
    }

   


    const getSmoothShipIconSize = (zoom) => {
        if (zoom <= 3) return 8;
        if (zoom >= 13) return 24;
        const breakpoints = [
            { z: 3, size: 14 },
            { z: 6, size: 14 },
            { z: 10, size: 14 },
            { z: 13, size: 14 }
        ];
        let lower = breakpoints[0], upper = breakpoints[breakpoints.length - 1];
        for (let i = 1; i < breakpoints.length; i++) {
            if (zoom < breakpoints[i].z) {
                lower = breakpoints[i - 1];
                upper = breakpoints[i];
                break;
            }
        }
        const t = (zoom - lower.z) / (upper.z - lower.z);
        return lower.size + t * (upper.size - lower.size);
    };

    const getRealisticNoiseRadius = (noise) => {
        const thresholdDb = 50;
        const radius = Math.pow(10, (noise - thresholdDb) / 20);
        return Math.max(50, Math.min(radius, 50000));
    };

    const metersToPixelsAtLatitude = (meters, latitude, zoom) => {
        const earthCircumference = 40075016.686;
        const latitudeRadians = latitude * Math.PI / 180;
        const metersPerPixel = earthCircumference * Math.cos(latitudeRadians) / Math.pow(2, zoom + 8);
        return meters / metersPerPixel;
    };

    function parseDbTimestamp(str) {
        if (!str) return null;
        if (str.includes("T")) return new Date(str);
        return new Date(str.replace(" ", "T") + "Z");
    }


    const handlePrevDay = () => {
        if (!selectedMinDate) return;
        const prevDay = new Date(selectedMinDate);
        prevDay.setDate(prevDay.getDate() - 1);
        setSelectedMinDate(prevDay);
        fetchDataForDate(prevDay);
    };

    const handleNextDay = () => {
        if (!selectedMinDate) return;
        const nextDay = new Date(selectedMinDate);
        nextDay.setDate(nextDay.getDate() + 1);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (nextDay > today) return;
        setSelectedMinDate(nextDay);
        fetchDataForDate(nextDay);
    };


    const REGION_UK = {
        minLat: -30,
        maxLat: 63,
        minLng: -130,
        maxLng: 10
    };

    const REGION_SPAIN_EUROPE = {
        minLat: 26,
        maxLat: 47,
        minLng: -13,
        maxLng: 10
    };

    const REGION_NORTH_EUROPE = {
        minLat: 47,
        maxLat: 54.2,
        minLng: 10,
        maxLng: 20
    };

    const REGION_NORTH = {
        minLat: 54.2,
        maxLat: 63,
        minLng: 3,
        maxLng: 20
    };


    const REGION_SJEVERNI_JADRAN = {
        minLat: 42,
        maxLat: 47,
        minLng: 10,
        maxLng: 20
    };

    const REGION_JUZNI_JADRAN = {
        minLat: 26,
        maxLat: 42,
        minLng: 10,
        maxLng: 20
    };



    const REGION_HAIFA_SJEVER = {
        minLat: 38,
        maxLat: 47,
        minLng: 20,
        maxLng: 38
    };

    const REGION_HAIFA_JUG = {
        minLat: -40,
        maxLat: 48,
        minLng: 0,
        maxLng: 160
    };


    const REGION_HAIFA = {
        minLat: 30,
        maxLat: 42,
        minLng: 28,
        maxLng: 37
    };

    const REGION_JADRAN = {
        minLat: 40,
        maxLat: 47,
        minLng: 12,
        maxLng: 20
    };


    const UK_EXCLUDED_REGIONS = [
        {
            minLat: 36.5,
            maxLat: 39,
            minLng: -79,
            maxLng: -76
        },
        {
            minLat: 33.88,
            maxLat: 34.21,
            minLng: -78,
            maxLng: -77.91
        },
        {
            minLat: 28.38,
            maxLat: 29.15,
            minLng: -82.15,
            maxLng: -80.95
        }
    ];

    const isInUKExcludedRegion = (lat, lng) => {
        return UK_EXCLUDED_REGIONS.some(r =>
            lat >= r.minLat &&
            lat <= r.maxLat &&
            lng >= r.minLng &&
            lng <= r.maxLng
        );
    };


    const isInRegion = (ship, region) => {
        return ship.LATITUDE >= region.minLat && ship.LATITUDE <= region.maxLat &&
            ship.LONGITUDE >= region.minLng && ship.LONGITUDE <= region.maxLng;
    };


    const fetchDataForDate = (date) => {
        if (!date) return;
        setIsLoading(true);

        if (fetchController.current) {
            fetchController.current.abort();
        }
        const controller = new AbortController();
        fetchController.current = controller;

        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const day = date.getDate().toString().padStart(2, "0");
        const from = `${year}-${month}-${day} 00:00:00`;
        const to = `${year}-${month}-${day} 23:59:59`;

        fetch(`${API_BASE}/api/ais-history?from=${from}&to=${to}&limit=10000000`, {
            signal: controller.signal
        })
            .then(res => res.json())
            .then(data => {
                const mappedData = data.map(row => ({
                    MMSI: row.mmsi,
                    LATITUDE: row.latitude,
                    LONGITUDE: row.longitude,
                    SPEED: row.speed,
                    NAVSTAT: row.navstat,
                    NAME: row.name,
                    AISTYPE: row.type,
                    A: row.a,
                    B: row.b,
                    COURSE: row.course,
                    HEADING: row.heading,
                    timestamp: row.timestamp,
                    DRAUGHT: parseFloat(row.draught ?? row.DRAUGHT ?? 0),

                }));
                setShipData(mappedData);

                const times = mappedData.map(row =>
                    row.timestamp ? parseDbTimestamp(row.timestamp) : null
                ).filter(d => d).sort((a, b) => a - b);

                if (times.length > 0) {
                    setCurrentTime(times[0]);
                    setMinTime(times[0].getTime());
                    setMaxTime(times[times.length - 1].getTime());
                } else {
                    setCurrentTime(null);
                    setMinTime(null);
                    setMaxTime(null);
                }
            })
            .catch(err => {
                if (err.name === "AbortError") {
                    return;
                }
                console.error("API error:", err);
                setShipData([]);
                setCurrentTime(null);
                setMinTime(null);
                setMaxTime(null);
            })
            .finally(() => {
                setIsLoading(false);
                fetchController.current = null;
            });
    };





    const getDateExtent = (rows) => {
        if (!rows || rows.length === 0) return { min: null, max: null };
        let minTs = Infinity, maxTs = -Infinity;
        for (let i = 0; i < rows.length; i++) {
            const t = rows[i]?.timestamp;
            if (!t) continue;
            const ts = new Date(t).getTime();
            if (Number.isFinite(ts)) {
                if (ts < minTs) minTs = ts;
                if (ts > maxTs) maxTs = ts;
            }
        }
        return {
            min: minTs === Infinity ? null : new Date(minTs),
            max: maxTs === -Infinity ? null : new Date(maxTs),
        };
    };

    const [csvDateExtent, setCsvDateExtent] = useState({ min: null, max: null });


    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginError("");

        try {
            const res = await fetch(`${API_BASE}/api/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email: loginEmail,
                    password: loginPassword
                })
            });

            const data = await res.json();

            if (!data.success) {
                setLoginError(data.error || "Incorrect email or password");
                return;
            }
            sessionStorage.setItem("jwtToken", data.token);
            sessionStorage.setItem("user", JSON.stringify(data.user));

            setIsLoggedIn(true);


            setShowLoginModal(false);
            setLoginEmail("");
            setLoginPassword("");
        } catch (err) {
            setLoginError("Server error");
        }
    };


    const user = JSON.parse(sessionStorage.getItem("user"));





    const handleRegister = async (e) => {
        e.preventDefault();

        if (regPassword !== regPasswordConfirm) {
            setRegError("Passwords do not match.");
            return;
        }

        const strongPassword =
            /[A-Z]/.test(regPassword) &&
            /[0-9]/.test(regPassword) &&
            regPassword.length >= 8;

        if (!strongPassword) {
            setRegError("Password must be at least 8 characters long<br/>and include one uppercase letter and one number.");
            return;
        }


        if (!captchaToken) {
            setRegError("Please complete the captcha first.");
            return;
        }

        setRegError("");

        try {
            const res = await fetch(`${API_BASE}/api/register`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: regName,
                    surname: regSurname,
                    email: regEmail,
                    password: regPassword,
                    affiliation: regAffiliation,
                    intended_use: regIntendedUse,
                    role: "user",
                    captcha: captchaToken

                })
            });

            const data = await res.json();

            if (!data.success) {
                setRegError(data.error || "Registration failed");
                return;
            }


            if (data.success) {
                setShowRegisterSuccess(true);
            }

            if (openedFromAdmin) {
                setShowLoginModal(false);
                setOpenedFromAdmin(false);
                return;
            }

            setAuthMode("login");
            setLoginEmail(regEmail);
            setLoginPassword("");
        } catch (err) {
            setRegError("Server error");
        }
    };


    const generateHeatmapGeoJSON = async () => {
        const currentTimestamp = currentTime ? currentTime.getTime() : null;
        const allowedTypes = [30, 36, 37];
        for (let i = 60; i <= 89; i++) allowedTypes.push(i);

        const allowedAISNavstats = [0, 2, 3, 4, 7];
        const validShips = shipData.filter((ship) => {
            
            if (!allowedAISNavstats.includes(ship.NAVSTAT)) return false;
            if (typeof ship.SPEED !== "number" || ship.SPEED < 2) return false;

            if (!currentTimestamp || !ship[timeColumn]) return false;

            const shipTime = new Date(ship[timeColumn]).getTime();
            if (isNaN(shipTime)) return false;

            return shipTime <= currentTimestamp && shipTime >= currentTimestamp - timeThreshold;
        });



        const uniqueShips = Object.values(
            validShips.reduce((acc, ship) => {
                acc[ship.MMSI] = ship;
                return acc;
            }, {})
        );


        const createFeatures = (region) =>
            uniqueShips
                .filter(ship => {

                    if (isInUKExcludedRegion(ship.LATITUDE, ship.LONGITUDE)) {
                        return false;
                    }

                    return isInRegion(ship, region);
                })
                .flatMap((ship) => {
                    const heatmapZoom = 10;
                    const lat0 = ship.LATITUDE;
                    const lon0 = ship.LONGITUDE;

                    if (
                        typeof ship.SPEED !== "number" ||
                        typeof ship.A !== "number" ||
                        typeof ship.B !== "number" ||
                        typeof lat0 !== "number" ||
                        typeof lon0 !== "number"
                    ) return [];


                    let noise = calculateNoiseByModel(ship, selectedModel, selectedBand);


                    let originalNoise = noise;

                    if (!isFinite(originalNoise)) return [];
                    if (originalNoise < noiseMin || originalNoise > noiseMax) return [];

                    const shipDepth = 0;
                    const verticalDist = Math.max(1, Math.abs(selectedDepth - shipDepth));

                    noise = originalNoise - 20 * Math.log10(verticalDist);

                    if (!isFinite(noise)) return [];


                    const MIN_COMBINED_SPL = 165;

                    if (selectedModel === "AVERAGE") {
                        if (!isFinite(noise)) noise = MIN_COMBINED_SPL;
                        else noise = Math.max(noise, MIN_COMBINED_SPL);
                    }



                    const baseRadius = getRealisticNoiseRadius(noise);
                    const radius = Math.sqrt(baseRadius ** 2 + verticalDist ** 2);
                    const radiusPixels = metersToPixelsAtLatitude(radius, lat0, heatmapZoom);
                    if (!isFinite(radiusPixels)) return [];

                    const numRays = 50;
                    const angles = Array.from({ length: numRays }, (_, i) => i * 18);



                    const weight = Math.max(0, (noise - noiseMin) / (noiseMax - noiseMin)) / numRays;

                    const metersPerDegLat = 111320;
                    const metersPerDegLng = 111320 * Math.cos(lat0 * Math.PI / 180);

                    const rayRings = [0.25, 0.50, 0.75, 1.0];

                    let rayFeatures = [];

                    for (const ring of rayRings) {
                        const rayDistanceRing = baseRadius * ring;

                        for (const a of angles) {
                            const rad = (a * Math.PI) / 180;

                            const newLat = lat0 + (rayDistanceRing / metersPerDegLat) * Math.sin(rad);
                            const newLng = lon0 + (rayDistanceRing / metersPerDegLng) * Math.cos(rad);

                            if (ship.MMSI === 312004000) {
                                console.log(
                                    `%cRAY | MMSI ${ship.MMSI} | ring=${ring} | angle=${a}° | lat=${newLat} | lon=${newLng}`,
                                    "color: #00aaff; font-weight: bold;"
                                );
                            }

                            rayFeatures.push({
                                type: "Feature",
                                geometry: { type: "Point", coordinates: [newLng, newLat] },
                                properties: {
                                    noise,
                                    radius: radiusPixels,
                                    normalizedNoise: Math.max(0, (noise - noiseMin) / (noiseMax - noiseMin)),
                                    weight: weight / rayRings.length,
                                    angle: a,
                                    ring
                                }
                            });
                        }
                    }


                    const lonelyRadius = 25000;
                    let neighboringShips = 0;

                    for (const otherShip of uniqueShips) {
                        if (otherShip.MMSI === ship.MMSI) continue;

                        const dx = (otherShip.LONGITUDE - lon0) * 111320 * Math.cos(lat0 * Math.PI / 180);
                        const dy = (otherShip.LATITUDE - lat0) * 111320;
                        const dist = Math.sqrt(dx * dx + dy * dy);

                        if (dist < lonelyRadius) {
                            neighboringShips++;
                            break;
                        }
                    }

                    let boostFactor = 1;
                    if (neighboringShips === 0) {
                        boostFactor = 3;
                    }

                    rayFeatures.forEach(f => {
                        f.properties.weight *= boostFactor;
                    });

                    return rayFeatures;


                });



        const normalize = (features) => {
            if (!features.length) return features;

            features.forEach(f => {
                const n = f.properties.noise;
                let normalized = (n - noiseMin) / (noiseMax - noiseMin);
                f.properties.normalizedNoise = Math.max(0, Math.min(1, normalized));
            });

            return features;
        };

        async function filterDepthForRegion(featureCollection) {
            const features = featureCollection.features;
            if (!features.length) return featureCollection;

            const body = features.map(f => ({
                lat: f.geometry.coordinates[1],
                lon: f.geometry.coordinates[0]
            }));

            let depthData;
            try {
                const resp = await fetch(`${API_BASE}/api/depths`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(body)
                });
                depthData = await resp.json();


            } catch (err) {
                console.error("Depth API error:", err);
                return featureCollection;
            }

            if (!depthData.depths) return featureCollection;

            const filtered = features.filter((f, i) => {
                const d = depthData.depths[i];

                if (d === null || d === undefined || isNaN(d)) return false;

                if (d < depthSeaThreshold) return true;

                return false;
            });




            return {
                type: "FeatureCollection",
                features: filtered
            };
        }




        const sjeverni = await filterDepthForRegion({
            type: "FeatureCollection",
            features: normalize(createFeatures(REGION_SJEVERNI_JADRAN))
        });

        const juzni = await filterDepthForRegion({
            type: "FeatureCollection",
            features: normalize(createFeatures(REGION_JUZNI_JADRAN))
        });

        const jadranReg = await filterDepthForRegion({
            type: "FeatureCollection",
            features: normalize(createFeatures(REGION_JADRAN))
        });

        const haifaReg = await filterDepthForRegion({
            type: "FeatureCollection",
            features: normalize(createFeatures(REGION_HAIFA))
        });

        const haifaSjeverReg = await filterDepthForRegion({
            type: "FeatureCollection",
            features: normalize(createFeatures(REGION_HAIFA_SJEVER))
        });

        const haifaJugReg = await filterDepthForRegion({
            type: "FeatureCollection",
            features: normalize(createFeatures(REGION_HAIFA_JUG))
        });
        const ukReg = await filterDepthForRegion({
            type: "FeatureCollection",
            features: normalize(createFeatures(REGION_UK))
        });

        const spainRegion = await filterDepthForRegion({
            type: "FeatureCollection",
            features: normalize(createFeatures(REGION_SPAIN_EUROPE))
        });



        const northEuropeReg = await filterDepthForRegion({
            type: "FeatureCollection",
            features: normalize(createFeatures(REGION_NORTH_EUROPE))
        });

        const northReg = await filterDepthForRegion({
            type: "FeatureCollection",
            features: normalize(createFeatures(REGION_NORTH))
        });

        return {
            sjeverni_jadran: sjeverni,
            juzni_jadran: juzni,
            jadran: jadranReg,
            haifa: haifaReg,
            haifa_sjever: haifaSjeverReg,
            haifa_jug: haifaJugReg,
            uk: ukReg,
            spain_region: spainRegion,
            north_europe: northEuropeReg,
            north: northReg
        };


    };



   
    const [heatmapData, setHeatmapData] = useState({
            jadran: { features: [] },
            haifa: { features: [] },
            sjeverni_jadran: { features: [] },
            juzni_jadran: { features: [] },
            haifa_sjever: { features: [] },
            haifa_jug: { features: [] },
            uk: { features: [] },
            spain_region: { features: [] },
            north_europe: { features: [] },
            north: { features: [] }
        });
   
     useEffect(() => {
       if (!mapReady) return;
       if (!showHeatmap) return;
       if (shipData.length === 0) return;
   
       let cancelled = false;
   
       (async () => {
           setHeatmapLoading(true);
   
           const result = await generateHeatmapGeoJSON();
   
           if (!cancelled) {
               setHeatmapData(result);
   
               requestAnimationFrame(() => {
                   setHeatmapLoading(false);
               });
           }
       })();
   
       return () => {
           cancelled = true;
       };
   }, [
    mapReady,
    showHeatmap,
    shipData,
    timeColumn,
    showNavstat0,
    showNavstat1,
    currentTime,
    noiseMin,
    noiseMax,
    selectedBand,
    selectedModel,
    selectedDepth
]);


    const {

        sjeverni_jadran,
        juzni_jadran,
        haifa_sjever,
        haifa_jug,
        uk,
        spain_region,
        north_europe,
        north
    } = heatmapData;




    function generateSplGrid(rows, currentTimestamp, gridSize = 0.05) {
        const grid = {};

        const validShips = rows.filter(ship => {
            const shipTime = new Date(ship[timeColumn]).getTime();
            if (!shipTime || shipTime < currentTimestamp - timeThreshold || shipTime > currentTimestamp) return false;
            if (typeof ship.SPEED !== "number" || ship.SPEED <= 0) return false;
            return true;
        });



        for (const ship of validShips) {
            const speedMps = knotsToMps(ship.SPEED);
            const length = (ship.A || 0) + (ship.B || 0);
            const sourceSpl = calculateNoise(speedMps, length);

            const lat = Math.round(ship.LATITUDE / gridSize) * gridSize;
            const lon = Math.round(ship.LONGITUDE / gridSize) * gridSize;
            const key = `${lat.toFixed(3)},${lon.toFixed(3)}`;

            if (!grid[key]) grid[key] = [];
            grid[key].push(sourceSpl);
        }

        const finalGrid = {};
        for (const key in grid) {
            finalGrid[key] = Math.max(...grid[key]);
        }

        return finalGrid;
    }
    function handleExportSpl() {
        const rowsForDay = getAllRowsForSelectedDay();
        if (!rowsForDay || rowsForDay.length === 0) {
            alert("No data available for export.");
            return;
        }

        const stepMinutes = 10;
        const stepMs = stepMinutes * 60 * 1000;

        const dayExtent = getDateExtent(rowsForDay);
        let t = dayExtent.min.getTime();
        const allRows = [];

        while (t <= dayExtent.max.getTime()) {
            const grid = generateSplGrid(rowsForDay, t);

            for (const key in grid) {
                const [lat, lon] = key.split(",");
                allRows.push({
                    time: new Date(t).toISOString(),
                    lat,
                    lon,
                    spl: grid[key].toFixed(2)
                });
            }
            t += stepMs;
        }

        const csv = Papa.unparse(allRows);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", "spl_export.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }



    const adjustTime = (minutes) => {
        setCurrentTime((prevTime) => new Date(prevTime.getTime() + minutes * 60000));
    };

    useEffect(() => {
        setAvailableDaysSet(prev => new Set([...prev, ...fixedActiveDays]));
    }, []);


  


    useEffect(() => {
        const today = new Date();
        const year = today.getFullYear();
        const month = (today.getMonth() + 1).toString().padStart(2, "0");
        const day = today.getDate().toString().padStart(2, "0");
        const from = `${year}-${month}-${day} 00:00:00`;
        const to = `${year}-${month}-${day} 23:59:59`;

        fetch(`${API_BASE}/api/ais-history?from=${from}&to=${to}&limit=10000`)
            .then(res => res.json())
            .then(data => {
                if (!Array.isArray(data)) throw new Error("Data not array");

                const mappedData = data.map(row => ({
                    MMSI: row.mmsi,
                    LATITUDE: row.latitude,
                    LONGITUDE: row.longitude,
                    SPEED: row.speed,
                    NAVSTAT: row.navstat,
                    NAME: row.name,
                    AISTYPE: row.type,
                    A: row.a,
                    B: row.b,
                    DRAUGHT: parseFloat(row.draught ?? row.DRAUGHT ?? 0),
                    COURSE: row.course,
                    HEADING: row.heading,
                    timestamp: row.timestamp
                }));

                setShipData(mappedData);
                setShipData(mappedData);
                setTimeColumn("timestamp");

                const allTimes = mappedData.map(row => {
                    if (!row.timestamp) return null;
                    return parseDbTimestamp(row.timestamp);
                }).filter(date => date !== null).sort((a, b) => a - b);

                const today = new Date();
                const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
                const todayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);

                const todayTimes = mappedData
                    .map(row => row.timestamp ? parseDbTimestamp(row.timestamp) : null)
                    .filter(d => d && d >= todayStart && d <= todayEnd)
                    .sort((a, b) => a - b);

                if (todayTimes.length > 0) {
                    setCurrentTime(todayTimes[0]);
                    setMinTime(todayTimes[0].getTime());
                    setMaxTime(todayTimes[todayTimes.length - 1].getTime());
                    setSelectedMinDate(todayTimes[0]);
                    setSelectedMaxDate(todayTimes[todayTimes.length - 1]);
                } else if (allTimes.length > 0) {
                    setCurrentTime(allTimes[0]);
                    setMinTime(allTimes[0].getTime());
                    setMaxTime(allTimes[allTimes.length - 1].getTime());
                    setSelectedMinDate(allTimes[0]);
                    setSelectedMaxDate(allTimes[allTimes.length - 1]);
                }

            })

            .catch(err => console.error("API error: ", err));
    }, []);


    useEffect(() => {
        let interval;
        if (isPlaying) {
            interval = setInterval(() => {
                setCurrentTime((prevTime) => new Date(prevTime.getTime() + 60000));
            }, playSpeed);
        } else {
            clearInterval(interval);
        }
        return () => clearInterval(interval);
    }, [isPlaying, playSpeed]);

    useEffect(() => {
        const onKeyDown = (ev) => {
            if (ev.key === "Escape" && isPickingLocation) {
                setIsPickingLocation(false);
                const map = mapRef.current?.getMap();
                if (map) map.getCanvas().style.cursor = "";
                if (pickHintRef.current) pickHintRef.current.style.display = "none";
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [isPickingLocation]);



    useEffect(() => {
        if (currentTime) {
            const currentTimestamp = currentTime.getTime();

            const allowedTypes = [30, 36, 37];
            for (let i = 60; i <= 89; i++) allowedTypes.push(i);

            const allowedAISNavstats = [0, 2, 3, 4, 7];

            const newShipsAtTime = shipData.filter(row => {
                if (!row[timeColumn]) return false;

                if (!allowedTypes.includes(row.AISTYPE)) return false;

                if (!allowedAISNavstats.includes(row.NAVSTAT)) return false;

                const speedKnots = typeof row.SPEED === "number" ? row.SPEED : 0;
                const isAnchor = speedKnots < 2;
                const isUnderway = speedKnots >= 2;

                if (isAnchor && !showNavstat0) return false;
                if (isUnderway && !showNavstat1) return false;

                const shipTime = new Date(row[timeColumn]).getTime();
                if (isNaN(shipTime)) return false;

                return (
                    shipTime <= currentTimestamp &&
                    shipTime >= currentTimestamp - timeThreshold
                );
            });

            setActiveShips(() => {
                const updatedShips = {};
                newShipsAtTime.forEach(ship => {
                    updatedShips[String(ship.MMSI)] = ship;
                });
                return updatedShips;
            });
        }
    }, [currentTime, shipData, showNavstat0, showNavstat1]);




    useEffect(() => {
        setShipData([]);
        setShipDataRaw([]);
        setActiveShips({});
        setCurrentTime(null);
        setMinTime(null);
        setMaxTime(null);
        setSelectedMinDate(null);
        setSelectedMaxDate(null);
        setIsPlaying(false);
        setDataSource("api");
        setHeatmapBoost(1);
        setHeatmapData({
            type: "FeatureCollection",
            features: []
        });
        setMapKey(prev => prev + 1);
    }, [activeTab]);


    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") setShowShipModal(false);
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);

    useEffect(() => {
        setShowShipModal(false);
    }, [selectedShip]);

    useEffect(() => {
        const newMinSL = getDynamicMinSourceLevel();
        setDynamicMinSL(newMinSL);
    }, [activeShips, selectedModel, selectedBand, currentTime]);

    useEffect(() => {
        const map = mapRef.current?.getMap();
        if (!map) return;

        const onData = (e) => {
            if (!showMPA) return;
            if (e.sourceId === "mpa" && e.isSourceLoaded) {
                console.log("MPA data loaded.");
                setMpaLoading(false);
            }
        };

        map.on("sourcedata", onData);

        return () => map.off("sourcedata", onData);

    }, [showMPA]);


    useEffect(() => {
        const newMinSL = getDynamicMinSourceLevel();
        setDynamicMinSL(newMinSL);
    }, [activeShips, selectedModel, selectedBand, currentTime]);


    useEffect(() => {
        if (showLoginModal && authMode === "register") {
            setTimeout(() => {
                if (window.grecaptcha && document.getElementById("recaptcha-register")) {
                    window.grecaptcha.render("recaptcha-register", {
                        sitekey: "6LcWBh4sAAAAAFk5yuvXn7hkE0wV-uAmh1dQi2Xz",
                        callback: (token) => setCaptchaToken(token)
                    });
                }
            }, 50);
        }
    }, [showLoginModal, authMode]);


    useEffect(() => {
        if (showLoginModal) {
            if (authMode === "register") {
                setRegName("");
                setRegSurname("");
                setRegEmail("");
                setRegPassword("");
                setRegPasswordConfirm("");
                setRegAffiliation("");
                setRegIntendedUse("Academic use");
                setCaptchaToken("");
                setRegError("");
            }

            if (authMode === "login") {
                setLoginEmail("");
                setLoginPassword("");
                setLoginError("");
            }
        }
    }, [showLoginModal, authMode]);


    useEffect(() => {
        document.title = "Historical noise map data";
    }, []);


    useEffect(() => {
        if (!showLoginModal) {
            setShowPassword(false);
            setShowPasswordConfirm(false);
            setShowLoginPassword(false);
        }
    }, [showLoginModal]);

    useEffect(() => {
        setShowPassword(false);
        setShowPasswordConfirm(false);
        setShowLoginPassword(false);
    }, [authMode, openedFromAdmin]);

   
        
    
        useEffect(() => {
            const map = mapRef.current?.getMap();
            if (!map || !showHeatmap || !heatmapLoading) return;
    
            const loadedSources = new Set();
    
            const onSourceData = (e) => {
                if (
                    e.sourceId &&
                    HEATMAP_SOURCE_IDS.includes(e.sourceId) &&
                    e.isSourceLoaded
                ) {
                    loadedSources.add(e.sourceId);
    
                    if (loadedSources.size === HEATMAP_SOURCE_IDS.length) {
                        setHeatmapLoading(false);
                        map.off("sourcedata", onSourceData);
                    }
                }
            };
    
    
            return () => {
                
                map.off("sourcedata", onSourceData);
            };
        }, [heatmapLoading, showHeatmap]);
    /*
    useEffect(() => {
        const ships = Object.values(activeShips || {});

        const srv36_37 = ships.filter(ship =>
            ship.AISTYPE === 37
        );

        let atAnchor = 0;
        let usingEngine = 0;
        let unknown = 0;

        const navstatCount = {};

        srv36_37.forEach(ship => {
            const nav = ship.NAVSTAT;

            if (nav === 1 || nav === 5) atAnchor++;
            else if (nav === 0) usingEngine++;
            else unknown++;

            if (nav !== undefined && nav !== null) {
                navstatCount[nav] = (navstatCount[nav] || 0) + 1;
            }
        });

        console.group("🟢 SRV ICONS ON SCREEN — AIS 36 & 37 ONLY");

        console.log("TOTAL:", srv36_37.length);
        console.log("AT ANCHOR (NAVSTAT 1,5):", atAnchor);
        console.log("USING ENGINE (NAVSTAT 0):", usingEngine);
        console.log("OTHER / UNKNOWN NAVSTAT:", unknown);

        console.group("NAVSTAT distribution");
        Object.keys(navstatCount)
            .sort((a, b) => Number(a) - Number(b))
            .forEach(nav => {
                console.log(`NAVSTAT ${nav}: ${navstatCount[nav]} plovila`);
            });
        console.groupEnd();

        console.groupEnd();
    }, [activeShips]);

*/



    const getCellSizeDeg = (zoom) => {
        if (zoom <= 2) return 5;
        if (zoom <= 4) return 2;
        if (zoom <= 5) return 1;
        if (zoom <= 6) return 0.5;
        if (zoom <= 7) return 0.25;
        return 0;
    };


    const visibleShips = useMemo(() => {
        const ships = Object.values(activeShips);
        if (ships.length === 0) return [];

        if (zoomLevel >= 8) {
            return ships;
        }

        const cellSize = getCellSizeDeg(zoomLevel);
        const cellGrid = new Map();

        for (const ship of ships) {
            const x = Math.floor(ship.LONGITUDE / cellSize);
            const y = Math.floor(ship.LATITUDE / cellSize);
            const key = `${x}_${y}`;

            if (!cellGrid.has(key)) {
                cellGrid.set(key, []);
            }

            cellGrid.get(key).push(ship);
        }

        const result = [];

        const maxPerCell =
            zoomLevel <= 2 ? 1 :
                zoomLevel <= 4 ? 2 :
                    zoomLevel <= 6 ? 4 :
                        10;

        for (const cellShips of cellGrid.values()) {
            result.push(...cellShips.slice(0, maxPerCell));
        }

        return result;
    }, [activeShips, zoomLevel]);

    const getShipIcon = (type, course) => {
        let iconSrc = "IconPurple1.png";
        let size = 15;

        if (type >= 60 && type <= 69) {
            iconSrc = "IconBlue1.png";
            size = 22;
        } else if (type >= 70 && type <= 79) {
            iconSrc = "IconYellow1.png";
            size = 24;
        } else if (type >= 80 && type <= 89) {
            iconSrc = "IconGray1.png";
            size = 26;
        } else if (type === 36) {
            iconSrc = "IconRed1.png";
            size = 15;
        } else if (type === 37) {
            iconSrc = "IconOrange1.png";
            size = 15;
        } else if (type === 30) {
            iconSrc = "IconPurple1.png";
            size = 15;
        }



        return L.divIcon({
            html: `<img src="${iconSrc}" style="width:${size}px; height:${size}px; transform: rotate(${course}deg);"/>`,
            className: "custom-ship-icon",
            iconSize: [size, size],
            iconAnchor: [size / 2, size / 2],
            popupAnchor: [0, -size / 2]
        });
    };

    return (
        <div style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
            {showLoginModal && (

                <div
                    style={{
                        position: "fixed",
                        top: 0, left: 0, right: 0, bottom: 0,
                        background: "rgba(0,0,0,0.4)",
                        zIndex: 5000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}
                    onClick={closeLoginModal}
                >
                    <form
                        onClick={(e) => e.stopPropagation()}
                        onSubmit={authMode === "login" ? handleLogin : handleRegister}
                        style={{
                            background: "#fff",
                            padding: "35px 25px 25px 25px",
                            borderRadius: "14px",
                            boxShadow: "0 6px 24px rgba(0,0,0,0.21)",
                            minWidth: 365,
                            display: "flex",
                            flexDirection: "column",
                            gap: 12.5,
                            position: "relative"
                        }}
                    >

                        <div
                            onClick={closeLoginModal}
                            style={{
                                position: "absolute",
                                top: "10px",
                                right: "10px",
                                fontSize: "22px",
                                cursor: "pointer",
                                color: "#777",
                                fontWeight: "bold",
                                lineHeight: "1"
                            }}
                        >
                            ×
                        </div>


                        <h2 style={{ margin: 0, marginBottom: 12, textAlign: "center" }}>
                            {authMode === "login" ? "Login" : "Create account"}
                        </h2>

                        {authMode === "login" ? (
                            <>
                                <input
                                    type="email"
                                    placeholder="Email"
                                    value={loginEmail}
                                    onChange={e => setLoginEmail(e.target.value)}
                                    required
                                    style={{ padding: 8, fontSize: 16, borderRadius: 6, border: "1px solid #ccc" }}
                                />
                                <div style={{ position: "relative", width: "100%" }}>
                                    <input
                                        type={showLoginPassword ? "text" : "password"}
                                        placeholder="Password"
                                        value={loginPassword}
                                        onChange={e => setLoginPassword(e.target.value)}
                                        required
                                        style={{
                                            padding: "8px 36px 8px 9px",
                                            fontSize: 16,
                                            borderRadius: 6,
                                            border: "1px solid #ccc",
                                            width: "100%",
                                            boxSizing: "border-box"
                                        }}
                                    />

                                    <img
                                        src={showLoginPassword ? "/hide.png" : "/show.png"}
                                        alt="toggle visibility"
                                        onClick={() => setShowLoginPassword(prev => !prev)}
                                        style={{
                                            position: "absolute",
                                            right: 10,
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            width: 20,
                                            height: 20,
                                            cursor: "pointer",
                                            opacity: 0.7
                                        }}
                                    />
                                </div>


                                {loginError && (
                                    <div style={{ color: "red", textAlign: "center", fontSize: 14 }}>
                                        {loginError}
                                    </div>
                                )}

                                <button type="submit" style={{ ...buttonStyle, width: "100%" }}>
                                    Login
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setAuthMode("register")}
                                    style={{
                                        ...buttonStyle,
                                        backgroundColor: "#ccc",
                                        color: "#222",
                                        width: "100%"
                                    }}
                                >
                                    Create account
                                </button>
                            </>
                        ) : (
                            <>
                                <input
                                    type="text"
                                    placeholder="Name"
                                    value={regName}
                                    onChange={e => setRegName(e.target.value)}
                                    required
                                    style={{ padding: 8, fontSize: 16, borderRadius: 6, border: "1px solid #ccc" }}
                                />

                                <input
                                    type="text"
                                    placeholder="Surname"
                                    value={regSurname}
                                    onChange={e => setRegSurname(e.target.value)}
                                    required
                                    style={{ padding: 8, fontSize: 16, borderRadius: 6, border: "1px solid #ccc" }}
                                />

                                <input
                                    type="email"
                                    placeholder="Email"
                                    value={regEmail}
                                    onChange={e => setRegEmail(e.target.value)}
                                    required
                                    style={{ padding: 8, fontSize: 16, borderRadius: 6, border: "1px solid #ccc" }}
                                />


                                <div style={{ position: "relative", width: "100%" }}>
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        placeholder="Password"
                                        value={regPassword}
                                        onChange={e => setRegPassword(e.target.value)}
                                        required
                                        style={{
                                            padding: "8px 36px 8px 9px",
                                            fontSize: 16,
                                            borderRadius: 6,
                                            border: "1px solid #ccc",
                                            width: "100%",
                                            boxSizing: "border-box"
                                        }}
                                    />

                                    <img
                                        src={showPassword ? "/hide.png" : "/show.png"}
                                        alt="toggle visibility"
                                        onClick={() => setShowPassword(prev => !prev)}
                                        style={{
                                            position: "absolute",
                                            right: 10,
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            width: 20,
                                            height: 20,
                                            cursor: "pointer",
                                            opacity: 0.7
                                        }}
                                    />
                                </div>

                                <div style={{ position: "relative", width: "100%" }}>
                                    <input
                                        type={showPasswordConfirm ? "text" : "password"}
                                        placeholder="Confirm password"
                                        value={regPasswordConfirm}
                                        onChange={e => setRegPasswordConfirm(e.target.value)}
                                        required
                                        style={{
                                            padding: "8px 36px 8px 9px",
                                            fontSize: 16,
                                            borderRadius: 6,
                                            border: "1px solid #ccc",
                                            width: "100%",
                                            boxSizing: "border-box"
                                        }}
                                    />

                                    <img
                                        src={showPasswordConfirm ? "/hide.png" : "/show.png"}
                                        alt="toggle visibility"
                                        onClick={() => setShowPasswordConfirm(prev => !prev)}
                                        style={{
                                            position: "absolute",
                                            right: 10,
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            width: 20,
                                            height: 20,
                                            cursor: "pointer",
                                            opacity: 0.7
                                        }}
                                    />
                                </div>


                                <input
                                    type="text"
                                    placeholder="Affiliation (University / Institution)"
                                    value={regAffiliation}
                                    onChange={e => setRegAffiliation(e.target.value)}
                                    required
                                    style={{ padding: 8, fontSize: 16, borderRadius: 6, border: "1px solid #ccc" }}
                                />

                                <label style={{ marginTop: "5px", fontWeight: "bold" }}>Intended use:</label>

                                <select
                                    value={regIntendedUse}
                                    onChange={(e) => setRegIntendedUse(e.target.value)}
                                    style={{
                                        padding: 10,
                                        fontSize: 16,
                                        borderRadius: 6,
                                        border: "1px solid #ccc",
                                        width: "100%"
                                    }}
                                >
                                    <option value="Academic use">Academic use</option>
                                    <option value="Policy initiatives">Policy initiatives</option>
                                    <option value="Management of marine areas">Management of marine areas</option>
                                    <option value="General interest in marine environment">General interest in marine environment</option>
                                </select>

                                <div style={{ marginTop: "10px" }}>
                                    <div
                                        id="recaptcha-register"
                                        className="g-recaptcha"
                                        data-sitekey="6LcWBh4sAAAAAFk5yuvXn7hkE0wV-uAmh1dQi2Xz"
                                    ></div>

                                </div>

                                {regError && (
                                    <div
                                        style={{ color: "red", textAlign: "center" }}
                                        dangerouslySetInnerHTML={{ __html: regError }}
                                    ></div>
                                )}



                                <button type="submit" style={{ ...buttonStyle, width: "100%" }}>
                                    Register
                                </button>

                                {!openedFromAdmin && (
                                    <button
                                        type="button"
                                        onClick={() => setAuthMode("login")}
                                        style={{
                                            ...buttonStyle,
                                            backgroundColor: "#ccc",
                                            color: "#222",
                                            width: "100%"
                                        }}
                                    >
                                        Back to login
                                    </button>
                                )}
                            </>

                        )}
                    </form>
                </div>
            )}

            <button
                onClick={() => setIsSidebarOpen(true)}
                style={{
                    position: "absolute",
                    top: "20px",
                    left: "20px",
                    zIndex: 1200,
                    background: "#007bff",
                    color: "white",
                    border: "none",
                    padding: "10px 15px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "25px",
                    boxShadow: "0 2px 5px rgba(0,0,0,0.3)",
                    display: isSidebarOpen ? "none" : "block"
                }}
            >
                ☰
            </button>




            {isSidebarOpen && (
                <div style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "250px",
                    bottom: 0,
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    boxShadow: "2px 0 10px rgba(0,0,0,0.2)",
                    zIndex: 1100,
                    display: "flex",
                    flexDirection: "column",
                    padding: "20px",
                    transition: "all 0.3s ease",
                    fontFamily: "Arial, sans-serif"
                }}>
                    <img
                        src={`${process.env.PUBLIC_URL}/SHIPECHO_LOGO.png`}
                        alt="SHIPECHO"
                        style={{
                            width: "120px",
                            display: "block",
                            margin: "-10px auto -32px auto",
                            borderRadius: "12px"
                        }}
                    />

                    <button
                        onClick={() => setIsSidebarOpen(false)}
                        style={{
                            position: "absolute",
                            top: "18px",
                            right: "18px",
                            background: "transparent",
                            border: "none",
                            fontSize: "26px",
                            fontWeight: "bold",
                            color: "#777",
                            cursor: "pointer",
                            zIndex: 10,
                            lineHeight: "1"
                        }}
                        aria-label="Close"
                    >
                        ×
                    </button>


                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: user && user.role === "admin" ? "flex-start" : "center",
                            marginBottom: "18px",
                            marginTop: "12px",
                            marginRight: user && user.role === "admin" ? "30px" : "0px"
                        }}
                    >
                        {user && user.role === "admin" && (
                            <div
                                onClick={() => {
                                    loadUsers();
                                    setShowAdminPanel(true);
                                }}
                                style={{
                                    cursor: "pointer",
                                    fontSize: "30px",
                                    color: "#007bff",
                                    marginTop: "15px",
                                }}
                            >
                                👤
                            </div>
                        )}

                        <h1 style={{ marginBottom: "0px", color: "#007bff", textAlign: "center", width: "100%" }}>
                            Map list
                        </h1>
                    </div>



                    <button
                        onClick={() => {
                            navigate("/live_ais_data");
                            setIsSidebarOpen(false);
                        }}
                        style={{
                            ...buttonStyleSidebar,
                            backgroundColor: (
                                location.pathname === "/live_ais_data" ||
                                location.pathname === "/"
                            ) ? "#0056b3" : "#1485FF",
                            marginBottom: "10px"
                        }}
                    >
                        🚢 Live vessel
                    </button>

                    <button
                        onClick={() => {
                            navigate("/ais_past_data");
                            setIsSidebarOpen(false);
                        }}
                        style={{
                            ...buttonStyleSidebar,
                            backgroundColor: location.pathname === "/ais_past_data" ? "#0056b3" : "#1485FF",
                            marginBottom: "10px"
                        }}
                    >
                        📅 Historical data
                    </button>





                    <button
                        onClick={() => {
                            navigate("/sel_ais_data_all");
                            setIsSidebarOpen(false);
                        }}
                        style={{
                            ...buttonStyleSidebar,
                            backgroundColor: location.pathname === "/sel_ais_data_all" ? "#0056b3" : "#1485FF"
                        }}
                    >
                        Sound exposure level
                    </button>
                    <hr style={{ margin: "15px 0" }} />
                    {!user ? (
                        <button
                            onClick={openLogin}
                            style={{ ...buttonStyleSidebar, backgroundColor: "#00a", marginBottom: "10px" }}
                        >
                            Login/Register
                        </button>

                    ) : (
                        <button
                            onClick={() => {
                                sessionStorage.removeItem("jwtToken");
                                sessionStorage.removeItem("user");

                                setIsLoggedIn(false);

                                setShowLoginModal(false);
                                setAuthMode("login");

                                setMapKey(k => k + 1);
                            }}

                            style={{ ...buttonStyleSidebar, backgroundColor: "#b33", marginBottom: "10px" }}
                        >
                            Logout
                        </button>

                    )}


                    <button
                        onClick={() => window.open('https://www.sciencedirect.com/science/article/pii/S0964569126002954?via%3Dihub', '_blank')}
                        style={{ ...buttonStyleSidebar, backgroundColor: "#198754" }}
                    >
                        📄 Documentation
                    </button>

                    <div style={{ flex: 1 }} />

                    <div style={{ marginTop: "auto", paddingTop: 12 }}>
                        <a
                            href="https://marsci.haifa.ac.il/en/school/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                        <img
                            src={`${process.env.PUBLIC_URL}/CSMS.jpeg`}
                            alt="Leon H. Charney School of Marine Sciences"
                            style={{
                                width: "100%",
                                height: "auto",
                                display: "block",
                                borderRadius: "8px",
                                marginBottom: "10px",
                            }}
                        />
                        </a>
                        <div
                            style={{
                                position: "relative",
                                marginBottom: "10px",
                            }}
                        >

                            <div
                                style={{
                                    backgroundColor: "#fff",
                                    borderRadius: "8px",
                                    width: "100%",
                                    height: "100%",
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    zIndex: 1,
                                }}
                            ></div>
                            <a
                                href="https://marble.eu/"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                            <img
                                src={`${process.env.PUBLIC_URL}/marble_logo.png`}
                                alt="Marble Logo"
                                style={{
                                    width: "100%",
                                    height: "auto",
                                    display: "block",
                                    borderRadius: "8px",
                                    position: "relative",
                                    marginTop: "-10px",
                                    zIndex: 2,

                                }}
                            />
                            </a>
                        </div>


                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                marginTop: "-48px",
                            }}
                        >
                            <a
                                href="https://sites.google.com/edu.haifa.ac.il/anl"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                            <img
                                src={`${process.env.PUBLIC_URL}/ANL.png`}
                                alt="ANL - Haifa"
                                style={{ height: "140px", objectFit: "cover", objectPosition: "center top" }}
                            />
                            </a>
                            <a
                                href="https://uwin-labust.fer.hr/"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                            <img
                                src={`${process.env.PUBLIC_URL}/UWIN.png`}
                                alt="ANL - Haifa"
                                style={{ height: "140px", objectFit: "cover", objectPosition: "center top" }}
                            />
                            </a>

                        </div>
                    </div>


                </div>
            )}

            {showAdminPanel && (
                <div
                    style={{
                        position: "fixed",
                        top: 0, left: 0, right: 0, bottom: 0,
                        background: "rgba(0,0,0,0.4)",
                        zIndex: 6000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}
                    onClick={() => setShowAdminPanel(false)}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            position: "relative",
                            background: "#fff",
                            padding: "25px",
                            borderRadius: "12px",
                            width: "100%",
                            maxWidth: "1000px",
                            maxHeight: "80vh",
                            overflowY: "scroll",
                            boxShadow: "0 5px 20px rgba(0,0,0,0.3)"
                        }}
                    >
                        <button
                            type="button"

                            onClick={() => setShowAdminPanel(false)}
                            style={{
                                position: "absolute",
                                top: "18px",
                                right: "18px",
                                background: "transparent",
                                border: "none",
                                fontSize: "26px",
                                fontWeight: "bold",
                                color: "#777",
                                cursor: "pointer",
                                zIndex: 10,
                                lineHeight: "1"
                            }}
                            aria-label="Close"
                        >
                            ×
                        </button>

                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginBottom: "25px"
                            }}
                        >
                            <button
                                onClick={() => {
                                    setOpenedFromAdmin(true);
                                    setAuthMode("register");
                                    setShowAdminPanel(false);
                                    setShowLoginModal(true);
                                }}
                                style={{
                                    backgroundColor: "#007bff",
                                    color: "white",
                                    padding: "10px 20px",
                                    borderRadius: "10px",
                                    border: "none",
                                    cursor: "pointer",
                                    fontWeight: "bold",
                                    fontSize: "16px",
                                    boxShadow: "0 3px 8px rgba(0,0,0,0.2)"
                                }}
                            >
                                ➕ Create user account
                            </button>


                            <h2 style={{ flex: 1, textAlign: "center", margin: 0 }}>
                                👤 All registered users
                            </h2>

                            <div style={{ width: "160px" }}></div>
                        </div>

                        {adminError && (
                            <div style={{ color: "red", textAlign: "center" }}>{adminError}</div>
                        )}

                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr style={{ background: "#007bff", color: "white" }}>
                                    <th style={{ textAlign: "left", padding: "8px" }}>Name</th>
                                    <th style={{ textAlign: "left", padding: "8px" }}>Surname</th>
                                    <th style={{ textAlign: "left", padding: "8px" }}>Email</th>
                                    <th style={{ textAlign: "left", padding: "8px" }}>Affiliation</th>
                                    <th style={{ textAlign: "left", padding: "8px" }}>Use</th>
                                    <th style={{ textAlign: "left", padding: "8px" }}>Role</th>
                                    <th style={{ textAlign: "left", padding: "8px" }}>Action</th>
                                </tr>
                            </thead>

                            <tbody>
                                {adminUsers.map(u => (
                                    <tr key={u.id} style={{ borderBottom: "1px solid #ccc" }}>
                                        <td style={{ textAlign: "left", padding: "8px" }}>{u.name}</td>
                                        <td style={{ textAlign: "left", padding: "8px" }}>{u.surname}</td>
                                        <td style={{ textAlign: "left", padding: "8px" }}>{u.email}</td>
                                        <td style={{ textAlign: "left", padding: "8px" }}>{u.affiliation}</td>
                                        <td style={{ textAlign: "left", padding: "8px" }}>{u.intended_use}</td>
                                        <td style={{ textAlign: "left", padding: "8px" }}>{u.role}</td>
                                        <td style={{ textAlign: "left", padding: "8px" }}>
                                            <button
                                                onClick={() => {
                                                    setDeleteUserId(u.id);
                                                    setShowDeleteModal(true);
                                                }}
                                                style={{
                                                    background: "red",
                                                    color: "white",
                                                    padding: "5px 10px",
                                                    borderRadius: "6px",
                                                    cursor: "pointer",
                                                    border: "none"
                                                }}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>

                        </table>
                    </div>
                </div>
            )}


            {showCalcModal && (
                <div
                    style={{
                        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                        background: "rgba(0,0,0,0.4)", zIndex: 5001,
                        display: "flex", alignItems: "center", justifyContent: "center"
                    }}
                    onClick={() => setShowCalcModal(false)}
                >
                    <form
                        onSubmit={e => {
                            e.preventDefault();
                            handleCalculate();
                        }}
                        onClick={e => e.stopPropagation()}
                        style={{
                            position: "relative",
                            background: "#fff",
                            padding: "32px 28px",
                            borderRadius: "14px",
                            boxShadow: "0 6px 24px rgba(0,0,0,0.21)",
                            minWidth: 340,
                            display: "flex", flexDirection: "column", gap: 16
                        }}
                    >
                        <button
                            type="button"
                            onClick={() => setShowCalcModal(false)}
                            style={{
                                position: "absolute",
                                top: "18px",
                                right: "18px",
                                background: "transparent",
                                border: "none",
                                fontSize: "26px",
                                fontWeight: "bold",
                                color: "#777",
                                cursor: "pointer",
                                zIndex: 10,
                                lineHeight: "1"
                            }}
                            aria-label="Close"
                        >
                            ×
                        </button>

                        <h2 style={{ margin: 0, marginBottom: 12, textAlign: "center" }}>Calculate SPL</h2>
                        <input
                            type="number"
                            step="any"
                            placeholder="Latitude"
                            value={calcLat}
                            onChange={e => setCalcLat(e.target.value)}
                            required
                            style={{ padding: 8, fontSize: 16, borderRadius: 6, border: "1px solid #ccc" }}
                        />
                        <input
                            type="number"
                            step="any"
                            placeholder="Longitude"
                            value={calcLng}
                            onChange={e => setCalcLng(e.target.value)}
                            required
                            style={{ padding: 8, fontSize: 16, borderRadius: 6, border: "1px solid #ccc" }}
                        />
                        
                        {depthError && (
                            <div style={{ color: "red", fontSize: 14, textAlign: "center" }}>
                                {depthError}
                            </div>
                        )}

                        <button type="submit" style={{ ...buttonStyle, width: "100%" }}>Calculate</button>

                        {calcResult && (
                            <div style={{ marginTop: 18, fontSize: 16 }}>
                                <div><b>SPL:</b> {calcResult.spl?.toFixed(2)} dB re 1 μPa</div>
                            </div>
                        )}
                    </form>
                </div>
            )}



            {isLoading && (
                <div style={{
                    position: "fixed",
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: "rgba(255,255,255,0.7)",
                    zIndex: 3000,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    cursor: "wait"

                }}>
                    <div style={{
                        width: "70px",
                        height: "70px",
                        border: "8px solid #f3f3f3",
                        borderTop: "8px solid #007bff",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite"
                    }} />
                    <style>{`
        @keyframes spin {
            0% { transform: rotate(0deg);}
            100% { transform: rotate(360deg);}
        }
        `}</style>
                    <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "20px", color: "#007bff" }}>
                        Loading data...
                    </div>
                    <div style={{ fontWeight: "normal", fontSize: "13px", color: "#007bff", marginTop: "5px" }}>
                        This may take a bit longer due to the large amount of data.
                    </div>
                    <button
                        style={{
                            marginTop: "22px",
                            padding: "12px 28px",
                            borderRadius: "7px",
                            fontSize: "14px",
                            backgroundColor: "#b33",
                            color: "white",
                            border: "none",
                            fontWeight: "bold",
                            cursor: "pointer",
                            boxShadow: "0 2px 5px rgba(0,0,0,0.18)"
                        }}
                        onClick={() => {
                            if (fetchController.current) {
                                fetchController.current.abort();
                            }

                            setIsLoading(false);

                            setShipData([]);
                            setShipDataRaw([]);
                            setActiveShips({});

                            setHeatmapData({
                                type: "FeatureCollection",
                                features: []
                            });

                            setHeatmapLoading(false);
                            setMapKey(prev => prev + 1);

                            setCurrentTime(null);
                            setMinTime(null);
                            setMaxTime(null);
                        }}

                    >
                        Cancel
                    </button>
                </div>
            )}


            <div style={{
                position: "absolute",
                width: "1100px",
                top: 20,
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 1000,
                background: "rgba(248, 249, 250, 0.9)",
                padding: "20px",
                borderRadius: "10px",
                boxShadow: "0 4px 10px rgba(0, 0, 0, 0.15)",
                fontFamily: "Arial, sans-serif",
                color: "#333"
            }}>




                {activeTab === "controls1" && (

                    <>
                        <h1 style={{
                            textAlign: "left",
                            fontWeight: 2000,
                            fontSize: "1.8rem",
                            letterSpacing: "0.5px",
                            marginBottom: "12px",
                            color: "#155fa0",
                            textShadow: "0 1px 4px #e7ecf7, 0 1px 0 #fff",
                            marginTop: "0px"
                        }}>
                            Historical noise map data - AIS data
                        </h1>
                        <div style={{ display: "flex", justifyContent: "left", gap: "10px", marginBottom: "15px" }}>
                            <button
                                onClick={() => setActiveTab("controls1")}
                                style={{ ...buttonStyle, backgroundColor: activeTab === "controls1" ? "#0056b3" : "#1485FF" }}
                            >
                                Historical AIS data
                            </button>
                            <button
                                onClick={() => setActiveTab("controls2")}
                                style={{ ...buttonStyle, backgroundColor: activeTab === "controls2" ? "#0056b3" : "#1485FF" }}
                            >
                                Upload AIS (CSV format)
                            </button>
                           
                            {user?.role === "admin" && (
                                <button
                                    onClick={() => {
                                        setExportDate(null);
                                        setShowExportModal(true);
                                    }}
                                    style={{
                                        ...buttonStyle,
                                        backgroundColor: "#28a745",
                                        marginLeft: "15px"
                                    }}
                                >
                                    📤 Export AIS data
                                </button>



                            )}

                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "15px", marginBottom: "15px" }}>
                            <button onClick={handlePrevDay} style={buttonStyle}>⬅️ Day before</button>
                            <label style={{ marginTop: "7px", fontSize: "16px", fontWeight: "bold" }}>
                                Choose date:
                            </label>
                            <DatePicker
                                selected={selectedMinDate}
                                onChange={(date) => {
                                    setSelectedMinDate(date);
                                    setIsPlaying(false);
                                    fetchDataForDate(date);
                                }}
                                dateFormat="dd/MM/yyyy"
                                minDate={new Date(2021, 0, 1)}
                                maxDate={new Date()}
                                filterDate={(date) => {
                                    const key = toUTCDateKey(new Date(Date.UTC(
                                        date.getFullYear(), date.getMonth(), date.getDate()
                                    )));
                                    return availableDaysSet.has(key);
                                }}
                               
                                dayClassName={(date) => {
                                    const key = toUTCDateKey(new Date(Date.UTC(
                                        date.getFullYear(), date.getMonth(), date.getDate()
                                    )));
                                    return availableDaysSet.has(key) ? "" : "react-datepicker__day--disabled";
                                }}
                            />



                            <button
                                onClick={handleNextDay}
                                style={buttonStyle}
                                disabled={selectedMinDate && new Date(selectedMinDate).toDateString() === new Date().toDateString()}
                            >
                                Next day ➡️
                            </button>
                            <div style={{ marginLeft: "20px" }}>

                            </div>
                        </div>

                        <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
                            <div style={{ fontSize: "16px", fontWeight: "bold" }}>
                                {currentTime ? currentTime.toLocaleString(undefined, {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                    hour12: false
                                }) + " UTC" : ""}
                            </div>

                            <button onClick={() => adjustTime(-10)} style={getButtonStyle(hasData)} disabled={!hasData}>⏪ -10 min</button>
                            <button onClick={() => setIsPlaying(!isPlaying)} style={getButtonStyle(hasData)} disabled={!hasData}
>
                                {isPlaying ? "⏸️ Stop" : "▶️ Start"}
                            </button>
                            <button onClick={() => adjustTime(10)} style={getButtonStyle(hasData)} disabled={!hasData}>+10 min ⏩</button>

                            <div style={{ marginLeft: "20px", display: "flex", alignItems: "center", gap: "10px" }}>
                                <span style={{ fontWeight: "bold" }}>Slower</span>
                                <input
                                    type="range"
                                    min={10000}
                                    max={60000}
                                    step={5000}
                                    value={70000 - (playSpeed)}
                                    onChange={e => {
                                        const sliderVal = Number(e.target.value);
                                        setPlaySpeed(70000 - sliderVal);
                                    }}
                                    style={{ width: 180 }}
                                />

                                <span style={{ fontWeight: "bold" }}>Faster</span>
                            </div>




                        </div>
                    </>
                )}
                {activeTab === "controls2" && (
                    <>
                        <h1 style={{
                            textAlign: "left",
                            fontWeight: 2000,
                            fontSize: "1.8rem",
                            letterSpacing: "0.5px",
                            marginBottom: "12px",
                            color: "#155fa0",
                            textShadow: "0 1px 4px #e7ecf7, 0 1px 0 #fff",
                            marginTop: "0px"
                        }}>
                            Historical noise map data - csv file
                        </h1>
                        <div style={{ display: "flex", justifyContent: "left", gap: "10px", marginBottom: "15px" }}>
                            <button
                                onClick={() => setActiveTab("controls1")}
                                style={{ ...buttonStyle, backgroundColor: activeTab === "controls1" ? "#0056b3" : "#1485FF" }}
                            >
                                Historical AIS data
                            </button>
                            <button
                                onClick={() => setActiveTab("controls2")}
                                style={{ ...buttonStyle, backgroundColor: activeTab === "controls2" ? "#0056b3" : "#1485FF" }}
                            >
                                Upload AIS (CSV format)
                            </button>
                          

                            {user?.role === "admin" && (
                                <button
                                    onClick={() => {
                                        setExportDate(null);
                                        setShowExportModal(true);
                                    }}
                                    style={{
                                        ...buttonStyle,
                                        backgroundColor: "#28a745",
                                        marginLeft: "15px"
                                    }}
                                >
                                    📤 Export AIS data
                                </button>


                            )}


                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "20px", marginBottom: "15px" }}>
                            <label style={{ fontSize: "16px", fontWeight: "bold" }}>
                                Select CSV file:
                            </label>
                            <input type="file" accept=".csv" onChange={handleCSVUpload} />

                            {dataSource === "csv" && shipDataRaw.length > 0 && (
                                <>
                                    <label style={{ fontSize: "16px", fontWeight: "bold" }}>
                                        Choose date from CSV:
                                    </label>
                                    <DatePicker
                                        selected={selectedMinDate}
                                        onChange={(date) => {
                                            setSelectedMinDate(date);
                                            setIsPlaying(false);
                                            applyCsvDay(date);
                                        }}
                                        dateFormat="dd/MM/yyyy"
                                        minDate={csvDateExtent.min}
                                        maxDate={csvDateExtent.max}
                                    />
                                </>
                            )}

                        </div>


                        <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
                            <div style={{ fontSize: "16px", fontWeight: "bold" }}>
                                {currentTime ? currentTime.toLocaleString(undefined, {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                    hour12: false
                                }) + " UTC" : ""}
                            </div>

                            <button onClick={() => adjustTime(-10)} style={getButtonStyle(hasData)} disabled={!hasData}>⏪ -10 min</button>
                            <button onClick={() => setIsPlaying(!isPlaying)} style={getButtonStyle(hasData)} disabled={!hasData}
>
                                {isPlaying ? "⏸️ Stop" : "▶️ Start"}
                            </button>
                            <button onClick={() => adjustTime(10)} style={getButtonStyle(hasData)} disabled={!hasData}>+10 min ⏩</button>

                            <div style={{ marginLeft: "20px", display: "flex", alignItems: "center", gap: "10px" }}>
                                <span style={{ fontWeight: "bold" }}>Slower</span>
                                <input
                                    type="range"
                                    min={10000}
                                    max={60000}
                                    step={5000}
                                    value={70000 - (playSpeed)}
                                    onChange={e => {
                                        const sliderVal = Number(e.target.value);
                                        setPlaySpeed(70000 - sliderVal);
                                    }}
                                    style={{ width: 180 }}
                                />

                                <span style={{ fontWeight: "bold" }}>Faster</span>
                            </div>


                            <div style={{ marginLeft: "20px" }}>

                            </div>
                        </div>
                    </>
                )}


                {minTime && maxTime && (
                    <div style={{ marginTop: "15px", marginBottom: "-17px" }}>
                        <input
                            type="range"
                            min={minTime}
                            max={maxTime}
                            step="60000"
                            value={currentTime ? currentTime.getTime() : minTime}
                            onChange={e => setCurrentTime(new Date(parseInt(e.target.value)))}
                            style={{ width: "80%", marginBottom: "15px" }}
                        />
                    </div>
                )}
            </div>

            {showExportModal && (
                <div
                    style={{
                        position: "fixed",
                        top: 0, left: 0, right: 0, bottom: 0,
                        background: "rgba(0,0,0,0.4)",
                        zIndex: 8000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}
                    onClick={() => setShowExportModal(false)}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: "white",
                            padding: "25px",
                            borderRadius: "12px",
                            width: "360px",
                            textAlign: "center",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.3)"
                        }}
                    >
                        <h3 style={{ marginBottom: "15px" }}>
                            Select day for CSV export
                        </h3>

                        <DatePicker
                            selected={exportDate}
                            onChange={setExportDate}
                            dateFormat="dd/MM/yyyy"
                            maxDate={new Date()}
                            placeholderText="Choose date"
                        />

                        <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
                            <button
                                style={{
                                    flex: 1,
                                    background: "#ccc",
                                    padding: "10px",
                                    borderRadius: "8px",
                                    border: "none",
                                    cursor: "pointer"
                                }}
                                onClick={() => setShowExportModal(false)}
                            >
                                Cancel
                            </button>

                            <button
                                style={{
                                    flex: 1,
                                    background: "#28a745",
                                    color: "white",
                                    padding: "10px",
                                    borderRadius: "8px",
                                    border: "none",
                                    cursor: "pointer",
                                    fontWeight: "bold"
                                }}
                                disabled={!exportDate}
                                onClick={async () => {
                                    setShowExportModal(false);
                                    setIsExporting(true);

                                    try {
                                        const y = exportDate.getFullYear();
                                        const m = String(exportDate.getMonth() + 1).padStart(2, "0");
                                        const d = String(exportDate.getDate()).padStart(2, "0");

                                        const from = `${y}-${m}-${d} 00:00:00`;
                                        const to = `${y}-${m}-${d} 23:59:59`;

                                        const response = await fetch(
                                                `${API_BASE}/api/export-csv?from=${from}&to=${to}`);

                                        const blob = await response.blob();
                                        const link = document.createElement("a");
                                        link.href = window.URL.createObjectURL(blob);
                                        link.download = `AIS_export_${d}-${m}-${y}.csv`;
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                    } catch (err) {
                                        alert("CSV export failed.");
                                    } finally {
                                        setIsExporting(false);
                                    }
                                }}
                            >
                                Export
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isExporting && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: "rgba(255,255,255,0.7)",
                        zIndex: 9000,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexDirection: "column",
                        cursor: "wait"

                    }}
                >
                    <div
                        style={{
                            width: "60px",
                            height: "60px",
                            border: "7px solid #f3f3f3",
                            borderTop: "7px solid #007bff",
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite"
                        }}
                    />
                    <style>{`
            @keyframes spin {
                0% { transform: rotate(0deg);}
                100% { transform: rotate(360deg);}
            }
        `}</style>

                    <div style={{
                        fontWeight: "bold",
                        fontSize: "20px",
                        color: "#007bff",
                        marginTop: "20px",
                        textAlign: "center"
                    }}>
                        Preparing CSV for download...
                        <div style={{
                            fontWeight: "normal",
                            fontSize: "13px",
                            color: "#007bff",
                            marginTop: "5px"
                        }}>
                            This may take a bit longer due to the large amount of data.
                        </div>
                    </div>
                </div>
            )}
            {heatmapLoading && !isPlaying && (
                <div style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: "rgba(255,255,255,0.6)",
                    zIndex: 2500,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    pointerEvents: "all",
                    cursor: "wait"
                                    }}>
                    <div style={{
                        fontSize: "26px",
                        fontWeight: "bold",
                        color: "#007bff",
                        marginBottom: "10px"
                    }}>
                        Loading noise map…
                    </div>

                    <div style={{
                        fontSize: "16px",
                        color: "#007bff"
                    }}>
                        Applying model & frequency band
                    </div>
                </div>
            )}

            {mpaLoading && (
                <div style={{
                    position: "fixed",
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: "rgba(255,255,255,0.7)",
                    zIndex: 3000,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    cursor: "wait"

                }}>
                    <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "25px", color: "#007bff" }}>
                        Loading marine protected areas...
                    </div>

                    <div style={{ fontWeight: "normal", fontSize: "18px", color: "#007bff", marginTop: "5px" }}>
                        This may take a few second.
                    </div>
                </div>
            )}

            {showDeleteModal && (
                <div
                    style={{
                        position: "fixed",
                        top: 0, left: 0, right: 0, bottom: 0,
                        background: "rgba(0,0,0,0.4)",
                        zIndex: 7000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}
                    onClick={() => setShowDeleteModal(false)}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: "white",
                            padding: "25px",
                            borderRadius: "12px",
                            width: "350px",
                            textAlign: "center",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.3)"
                        }}
                    >
                        <h3 style={{ marginBottom: "20px" }}>
                            Are you sure you want to delete this user?
                        </h3>

                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <button
                                style={{
                                    background: "red",
                                    color: "white",
                                    padding: "10px 20px",
                                    borderRadius: "8px",
                                    border: "none",
                                    cursor: "pointer",
                                    width: "45%"
                                }}
                                onClick={() => {
                                    deleteUser(deleteUserId);
                                    setShowDeleteModal(false);
                                }}
                            >
                                <h3 >
                                    Yes
                                </h3>
                            </button>

                            <button
                                style={{
                                    background: "#ccc",
                                    color: "#000",
                                    padding: "10px 20px",
                                    borderRadius: "8px",
                                    border: "none",
                                    cursor: "pointer",
                                    width: "45%"
                                }}
                                onClick={() => setShowDeleteModal(false)}
                            >
                                <h3 >
                                    No
                                </h3>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showRegisterSuccess && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "rgba(0,0,0,0.5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 9999
                    }}
                >
                    <div
                        style={{
                            backgroundColor: "white",
                            padding: "30px",
                            borderRadius: "10px",
                            width: "350px",
                            position: "relative",
                            textAlign: "center",
                            boxShadow: "0 4px 15px rgba(0,0,0,0.3)"
                        }}
                    >
                        <button
                            onClick={() => setShowRegisterSuccess(false)}
                            style={{
                                position: "absolute",
                                top: "18px",
                                right: "18px",
                                background: "transparent",
                                border: "none",
                                fontSize: "23px",
                                fontWeight: "bold",
                                color: "#777",
                                cursor: "pointer",
                                zIndex: 10,
                                lineHeight: "1"
                            }}
                        >
                            ×
                        </button>

                        <h2 style={{ marginBottom: "20px" }}>Successful registration!</h2>

                        <button
                            onClick={() => setShowRegisterSuccess(false)}
                            style={{
                                padding: "3px 25px",
                                backgroundColor: "#007bff",
                                color: "white",
                                border: "none",
                                borderRadius: "6px",
                                cursor: "pointer",
                                fontWeight: "bold",
                                marginTop: "5px"
                            }}
                        >
                            <h2> OK</h2>
                        </button>
                    </div>
                </div>
            )}

            <MapGL
                key={mapKey}
                ref={mapRef}
                mapLib={maplibregl}
                onClick={(e) => {
                    if (isPickingLocation) {
                        const { lng, lat } = e.lngLat;
                        setCalcLat(lat.toFixed(6));
                        setCalcLng(lng.toFixed(6));
                    

                        fetch(`${API_BASE}/api/depths`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify([{ lat, lon: lng }])
                        })
                            .then(res => res.json())
                            .then(data => {
                                if (data.depths && data.depths[0] !== null) {
                                    setActualDepth(data.depths[0]);
                                } else {
                                    setActualDepth(null);
                                }
                            })
                            .catch(err => {
                                console.error("Depth fetch error: ", err);
                                setActualDepth(null);
                            });

                        setIsPickingLocation(false);
                        const map = mapRef.current?.getMap();
                        if (map) map.getCanvas().style.cursor = "";
                        if (pickHintRef.current) pickHintRef.current.style.display = "none";
                        setShowCalcModal(true);
                        return;
                    }



                    setSelectedShip(null);
                    if (isSidebarOpen) setIsSidebarOpen(false);
                }}

                initialViewState={{
                    longitude: 25.9,
                    latitude: 40.72,
                    zoom: 4,
                    maxZoom: 10.2,
                    minZoom: 3.5
                }}
                style={{ flex: 1, width: "100%" }}
                mapStyle="https://api.maptiler.com/maps/0195ec7d-c124-7e8f-85b1-bb0602899c0e/style.json?key=sDvAXBg4VEXfXMSAABi9"

                onLoad={() => {
                    const map = mapRef.current.getMap();
                    setMapReady(true);

                    const layers = map.getStyle().layers;

                    if (layers.find(layer => layer.id === 'Depth labels')) {
                        map.moveLayer('Depth labels');
                    } else {
                        console.log("Layer 'Depth labels' does not exist yet.");
                    }
                    if (layers.find(layer => layer.id === 'Terrain RGB')) {
                        map.moveLayer('Terrain RGB');
                    } else {
                        console.log("Layer 'Terrain RGB' does not exist yet.");
                    }



                    if (layers.find(layer => layer.id === 'Lake')) {
                        map.moveLayer('Lake');
                    } else {
                        console.log("Layer 'Lake' does not exist yet.");
                    }

                    if (layers.find(layer => layer.id === 'Rivers')) {
                        map.moveLayer('Rivers');
                    } else {
                        console.log("Layer 'Rivers' does not exist yet.");
                    }

                    if (layers.find(layer => layer.id === 'Island labels')) {
                        map.moveLayer('Island labels');
                    } else {
                        console.log("Layer 'Island labels' does not exist yet.");
                    }
                }}
                onMove={(e) => {
                    const newZoom = e.viewState.zoom;
                    setZoomLevel(parseFloat(newZoom.toFixed(0)));
                }}
            >


                {showIcons && visibleShips.map((ship, index) => (
                    <Marker
                        key={index}
                        longitude={ship.LONGITUDE}
                        latitude={ship.LATITUDE}
                        anchor="center"
                        onClick={(e) => {
                            e.originalEvent.stopPropagation();
                            setSelectedShip(ship);
                        }}
                    >
                        <img
                            src={getShipIcon(ship.AISTYPE, ship.COURSE)?.options?.html?.match(/src="([^"]+)"/)?.[1] || "IconPurple1.png"}
                            alt="ship"
                            style={{
                                width: `${getSmoothShipIconSize(zoomLevel)}px`,
                                height: `${getSmoothShipIconSize(zoomLevel)}px`,
                                transform: `rotate(${ship.COURSE || 0}deg)`,
                                cursor: "pointer",
                                transition: "width 0.22s cubic-bezier(0.32, 0.72, 0.46, 1), height 0.22s cubic-bezier(0.32, 0.72, 0.46, 1)",
                                willChange: "width, height"
                            }}
                        />
                    </Marker>
                ))}



                {selectedShip && (
                    <Popup
                        longitude={selectedShip.LONGITUDE}
                        latitude={selectedShip.LATITUDE}
                        anchor="top"
                        closeOnClick={false}
                        onClose={() => setSelectedShip(null)}
                        style={{ maxWidth: "300px", minWidth: "300px" }}

                    >
                        <div style={{ lineHeight: "1.5", fontSize: "13.5px" }}>
                            <strong>{selectedShip.NAME || "Unknown vessel"}</strong><br />
                            MMSI: {selectedShip.MMSI}<br />
                            Type: {getShipDescription(selectedShip.AISTYPE)}<br />
                            Speed: {selectedShip.SPEED?.toFixed?.(1) || 0} knots<br />
                            Course: {selectedShip.COURSE}°<br />
                            Time: {selectedShip[timeColumn]}<br />

                            {(() => {
                                const length = getVesselLength(selectedShip);
                                const beam = (Number(selectedShip.C) || 0) + (Number(selectedShip.D) || 0);
                                const draftInfo = getEstimatedDraught(selectedShip);
                                let noise;

                                noise = calculateNoiseByModel(selectedShip, selectedModel, selectedBand);

                                return (
                                    <>

                                        <span>Length: {length.toFixed(1)} m</span><br />
                                        <span>Draft: {draftInfo.label}</span><br />
                                        {selectedShip.SPEED < 2 ? (
                                            <span>Noise Source Level: N/A</span>
                                        ) : noise === null ? (
                                            <span>Noise Source Level: not supported for this type of vessel</span>
                                        ) : (
                                            <span>{selectedBand === "Broadband"
                                                ? "Noise Source Level: " + noise.toFixed(2) + " dB re 1 μPa @ 1m"
                                                : "Noise Source Level: " + noise.toFixed(2) + " dB re 1 μPa @ 1m"
                                            }
                                            </span>
                                        )}

                                    </>
                                );
                            })()}
                        </div>
                    </Popup>

                )}
                <div style={{
                    position: "absolute",
                    bottom: 67,
                    right: 10,
                    backgroundColor: "white",
                    padding: "10px",
                    borderRadius: "5px",
                    boxShadow: "0 2 5px rgba(0,0,0,0.3)",
                    zIndex: 1000,
                    fontSize: "14px",
                    marginBottom: "170px",
                }}>

                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <label>
                            <input
                                type="checkbox"
                                checked={showIcons}
                                onChange={() => setShowIcons(prev => !prev)}
                            />
                            Vessel Icons
                        </label>

                        <label>
                            <input
                                type="checkbox"
                                checked={showHeatmap}
                                onChange={() => setShowHeatmap(prev => !prev)}
                            />
                            Noise Heatmap
                        </label>

                        <label style={{ cursor: "pointer" }}>
                            <input
                                type="checkbox"
                                checked={showMPA}
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    setShowMPA(checked);

                                    if (checked) {
                                        setMpaLoading(true);
                                    } else {
                                        setMpaLoading(false);
                                    }
                                }}
                            />

                            Marine protected areas
                        </label>

                    </div>
                </div>

                <div style={{
                    position: "absolute",
                    bottom: 14,
                    right: 10,
                    backgroundColor: "white",
                    padding: "10px",
                    borderRadius: "5px",
                    boxShadow: "0 2 5px rgba(0,0,0,0.3)",
                    zIndex: 1000,
                    fontSize: "14px",
                    marginBottom: "150px",
                }}>

                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <label>


                            <input
                                type="checkbox"
                                checked={showNavstat0}
                                onChange={() => setShowNavstat0(prev => !prev)}
                            />
                            ⚓️Vessel at anchor
                        </label>
                        <label>
                            <input
                                type="checkbox"
                                checked={showNavstat1}
                                onChange={() => setShowNavstat1(prev => !prev)}
                            />
                            🚢Underway using engine vessel
                        </label>
                    </div>
                </div>



                {showShipIconLegend && (() => {
                    const relevantTypes = getRelevantVesselTypesForModel(selectedModel);
                    const vesselLegend = [
                        { name: "Passenger", label: "Passenger vessels", icon: "IconBlue1.png" },
                        { name: "Cargo", label: "Cargo vessels", icon: "IconYellow1.png" },
                        { name: "Tankers", label: "Tankers", icon: "IconGray1.png" },
                        { name: "Sailing", label: "Sailing vessel", icon: "IconRed1.png" },
                        { name: "Pleasure", label: "Pleasure Craft", icon: "IconOrange1.png" },
                        { name: "Fishing", label: "Fishing vessel", icon: "IconPurple1.png" }
                    ];

                    return (
                        <div
                            style={{
                                position: "absolute",
                                bottom: "40px",
                                left: "10px",
                                backgroundColor: "rgb(255, 255, 255)",
                                padding: "10px",
                                borderRadius: "10px",
                                boxShadow: "0 2px 5px rgba(0, 0, 0, 0.3)",
                                zIndex: 1000,
                                fontSize: "14px",
                            }}
                        >
                            <div style={{ textAlign: "center", marginBottom: "6px" }}>
                                <strong>Vessel type</strong>
                            </div>

                            {vesselLegend.map(v => (
                                <div key={v.name} style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}>
                                    <img
                                        src={v.icon}
                                        style={{ width: "15px", verticalAlign: "middle", marginRight: "5px" }}
                                        alt={v.label}
                                    />
                                    <span>{v.label}</span>
                                    {relevantTypes.includes(v.name) && (
                                        <span style={{ color: "green", fontWeight: "bold", marginLeft: "6px" }}>✔</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    );
                })()}


{showHeatmapLegend && (
    <div
        style={{
            position: "absolute",
            bottom: "40px",
            right: "10px",
            backgroundColor: "rgb(255, 255, 255)",
            padding: "12px",
            borderRadius: "10px",
            boxShadow: "0 2px 5px rgba(0, 0, 0, 0.3)",
            zIndex: 1000,
            fontSize: "14px",
            width: "280px",
        }}
    >
        <h4 style={{ textAlign: "center", marginBottom: "12px", fontSize: "13px" }}>
            Sound Pressure Level (SPL, dB re 1 μPa)
        </h4>

        <div
            style={{
                width: "100%",
                height: "20px",
                background: "linear-gradient(to right, rgb(77, 255, 77) 0%, #00cc00 25%, #ccff00 45%, #ffff00 65%, #ffcc00 75%, #ff8000 85%, #ff3300 95%, #cc0000 100%)",
                borderRadius: "5px",
                marginBottom: "6px"
            }}
        />

        <div
            style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "11px",
                color: "#333",
                fontWeight: "bold"
            }}
        >
            {legendTicks.map((tick, i) => (
                <span key={i}>{tick}</span>
            ))}
        </div>
    </div>
)}

                <div style={{
                    position: "absolute",
                    bottom: "41px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    zIndex: 2000,
                    backgroundColor: "rgba(255,255,255,0.9)",
                    padding: "12px 20px",
                    borderRadius: "8px",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                    display: "flex",
                }}>
                    <div>
                        <div style={{ fontWeight: "bold", fontSize: "14px", color: "black", marginBottom: "5px" }}>Frequency Bands:</div>
                        <div style={{ display: "flex", gap: "6px" }}>
                            <button
                                style={{
                                    ...buttonStyle,
                                    height: "50px",
                                    backgroundColor: selectedBand === "63Hz" ? "#0056b3" : "#1485FF",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    lineHeight: "1.2",
                                    padding: "6px 12px"
                                }}
                                onClick={() => setSelectedBand("63Hz")}
                            >
                                63 Hz
                            </button>

                            <button
                                style={{
                                    ...buttonStyle,
                                    height: "50px",
                                    backgroundColor: selectedBand === "125Hz" ? "#0056b3" : "#1485FF",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    lineHeight: "1.2",
                                    padding: "6px 12px"
                                }}
                                onClick={() => setSelectedBand("125Hz")}
                            >
                                125 Hz
                            </button>

                            <button
                                style={{
                                    ...buttonStyle,
                                    height: "50px",
                                    backgroundColor: selectedBand === "Broadband" ? "#0056b3" : "#1485FF",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    lineHeight: "1.2",
                                    padding: "6px 12px"
                                }}
                                onClick={() => setSelectedBand("Broadband")}
                            >
                                Broadband<br />(20–2000 Hz)
                            </button>
                        </div>
                    </div>

                </div>


                <div style={selectContainerStyle}>
                    <label htmlFor="model-select" style={labelStyle}>Model:</label>

                    <select
                        id="model-select"
                        value={selectedModel}
                        onChange={(e) => {
                            setSelectedModel(e.target.value);
                            setIsSelectOpen(false);
                        }}
                        onMouseDown={() => setIsSelectOpen(true)}
                        onBlur={() => setIsSelectOpen(false)}
                        style={selectBoxStyle}
                    >

                        {isSelectOpen ? (
                            <>
                                <option value="RANDI">RANDI 3.1 (Breeding et al., 1996)</option>
                                <option value="JOMOPANS">JOMOPANS-ECHO (MacGillivray & de Jong, 2021)</option>
                                <option value="LBDS">LBDS (Simard et al., 2016)</option>
                                <option value="AQUO">AQUO (Audoly & Rizzuto, 2015)</option>
                                <option value="SMALL_VESSEL">SRV MODEL</option>
                                <option value="AVERAGE">COMBINED (Displays the average of all models for that vessel type )</option>

                            </>
                        ) : (
                            <>
                                <option value="RANDI">RANDI</option>
                                <option value="JOMOPANS">JOMOPANS</option>
                                <option value="LBDS">LBDS</option>
                                <option value="AQUO">AQUO</option>
                                <option value="SMALL_VESSEL">SRV MODEL</option>
                                <option value="AVERAGE">COMBINED</option>


                            </>
                        )}
                    </select>
                </div>






                <Source
                    id="mask-land"
                    type="vector"
                    tiles={["https://api.maptiler.com/tiles/land/{z}/{x}/{y}.pbf?key=sDvAXBg4VEXfXMSAABi9"]}
                >
                    <Layer
                        id="mask-land-fill"
                        source="mask-land"
                        source-layer="land"
                        type="fill"
                        paint={{
                            "fill-color": "#E0E0E0",
                            "fill-opacity": 0.9999999999999
                        }}
                    />
                </Source>



                {showHeatmap && (
                    <>
                        <Source id="heatmap-jadran-sjever" type="geojson" data={sjeverni_jadran}>
                            <Layer
                                id="heatmap-jadran-sjever"
                                type="heatmap"
                                beforeId="mask-land-fill"
                                paint={{
                                    "heatmap-weight": ["get", "weight"],
                                    "heatmap-radius": [
                                        "interpolate", ["linear"], ["zoom"],
                                        3, ["*", ["get", "radius"], isSRV ? 0.06 : 0.04],
4, ["*", ["get", "radius"], isSRV ? 0.07 : 0.05],
5, ["*", ["get", "radius"], isSRV ? 0.08 : 0.06],
6, ["*", ["get", "radius"], isSRV ? 0.11 : 0.10],
7, ["*", ["get", "radius"], isSRV ? 0.18 : 0.16],
8, ["*", ["get", "radius"], isSRV ? 0.30 : 0.24],
9, ["*", ["get", "radius"], isSRV ? 0.65 : 0.50],
10, ["*", ["get", "radius"], isSRV ? 1.35 : 1.00],
11, ["*", ["get", "radius"], isSRV ? 2.55 : 1.95],
12, ["*", ["get", "radius"], isSRV ? 4.80 : 3.80],
13, ["*", ["get", "radius"], isSRV ? 8.80 : 7.00],
                                    ],
                                    "heatmap-intensity": [
                                        "interpolate", ["linear"], ["zoom"],
                                        2, 0.003 * intensityMultiplier,
                                        10, 1.1 * intensityMultiplier,
                                    ],
                                    "heatmap-color": [
                                        "interpolate", ["linear"], ["min", 
                                        ["heatmap-density"], 0.25],
                                        0, "rgba(0, 128, 0, 0)",
                                        0.1, "#00cc00",
                                        0.25, "#ccff00",
                                        0.4, "#ffff00",
                                        0.55, "#ffcc00",
                                        0.7, "#ff8000",
                                        0.75, "#ff8000"
                                    ],
                                    "heatmap-opacity": 0.8
                                }}
                            />
                        </Source>

                        <Source id="heatmap-jadran-jug" type="geojson" data={juzni_jadran}>
                            <Layer
                                id="heatmap-jadran-jug"
                                type="heatmap"
                                beforeId="mask-land-fill"
                                paint={{
                                    "heatmap-weight": ["get", "weight"],
                                    "heatmap-radius": [
                                        "interpolate", ["linear"], ["zoom"],
                                        3, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.04],
                                        4, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.05],
                                        5, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.06],
                                        6, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.1],
                                        7, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.16],
                                        8, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.24],
                                        9, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.5],
                                        10, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 1.0],
                                        11, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 1.95],
                                        12, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 3.8],
                                        13, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 7],
                                    ],
                                    "heatmap-intensity": [
                                        "interpolate", ["linear"], ["zoom"],
                                        2, 0.003 * intensityMultiplier,
                                        10, 1.5 * intensityMultiplier,
                                    ],
                                    "heatmap-color": [
                                        "interpolate", ["linear"], ["min", ["heatmap-density"], 0.75],
                                        0, "rgba(0, 128, 0, 0)",
                                        0.1, "#00cc00",
                                        0.25, "#ccff00",
                                        0.4, "#ffff00",
                                        0.55, "#ffcc00",
                                        0.7, "#ff8000",
                                        0.75, "#ff8000"
                                    ],
                                    "heatmap-opacity": 0.8
                                }}
                            />
                        </Source>

                        <Source id="ship-noise-heatmap-haifa-jug" type="geojson" data={haifa_jug}>
                            <Layer
                                id="ship-noise-heat-haifa-jug"
                                type="heatmap"
                                beforeId="mask-land-fill"
                                paint={{
                                    "heatmap-weight": ["get", "weight"],
                                    "heatmap-radius": [
                                        "interpolate", ["linear"], ["zoom"],
                                        3, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.04],
                                        4, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.05],
                                        5, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.06],
                                        6, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.1],
                                        7, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.16],
                                        8, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.24],
                                        9, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.5],
                                        10, ["*", ["get", "radius"],],

                                    ],
                                    "heatmap-intensity": [
                                        "interpolate", ["linear"], ["zoom"],
                                        2, 0.01 * intensityMultiplier,
                                        10, 1.6 * intensityMultiplier,
                                    ],
                                    "heatmap-color": [
                                        "interpolate", ["linear"], ["min", ["heatmap-density"], 0.75],
                                        0, "rgba(0, 128, 0, 0)",
                                        0.1, "#00cc00",
                                        0.25, "#ccff00",
                                        0.4, "#ffff00",
                                        0.55, "#ffcc00",
                                        0.7, "#ff8000",
                                        0.75, "#ff8000"
                                    ],
                                    "heatmap-opacity": 0.8
                                }}
                            />
                        </Source>

                        <Source id="ship-noise-heatmap-haifa-sjever" type="geojson" data={haifa_sjever}>
                            <Layer
                                id="ship-noise-heatmap-haifa-sjever"
                                type="heatmap"
                                beforeId="mask-land-fill"
                                paint={{
                                    "heatmap-weight": ["get", "weight"],
                                    "heatmap-radius": [
                                        "interpolate", ["linear"], ["zoom"],
                                        3, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.04],
                                        4, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.05],
                                        5, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.06],
                                        6, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.1],
                                        7, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.16],
                                        8, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.24],
                                        9, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.5],
                                        10, ["*", ["get", "radius"],],

                                    ],
                                    "heatmap-intensity": [
                                        "interpolate", ["linear"], ["zoom"],
                                        2, 0.01 * intensityMultiplier,
                                        10, 1.7 * intensityMultiplier,
                                    ],
                                    "heatmap-color": [
                                        "interpolate", ["linear"], ["min", ["heatmap-density"], 0.75],
                                        0, "rgba(0, 128, 0, 0)",
                                        0.1, "#00cc00",
                                        0.25, "#ccff00",
                                        0.4, "#ffff00",
                                        0.55, "#ffcc00",
                                        0.7, "#ff8000",
                                        0.75, "#ff8000"
                                    ],
                                    "heatmap-opacity": 0.8
                                }}
                            />
                        </Source>

                        <Source id="heatmap-uk" type="geojson" data={uk}>
                            <Layer
                                id="heatmap-uk"
                                type="heatmap"
                                beforeId="mask-land-fill"
                                paint={{
                                    "heatmap-weight": ["get", "weight"],
                                    "heatmap-radius": [
                                        "interpolate", ["linear"], ["zoom"],
                                        3, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.04],
                                        4, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.05],
                                        5, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.06],
                                        6, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.1],
                                        7, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.16],
                                        8, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.24],
                                        9, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.5],
                                        10, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 1.0],
                                        11, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 1.95],
                                        12, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 3.8],
                                        13, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 7],
                                    ],
                                    "heatmap-intensity": [
                                        "interpolate", ["linear"], ["zoom"],
                                        2, 0.003 * intensityMultiplier,
                                        10, 1.2 * intensityMultiplier,
                                    ],
                                    "heatmap-color": [
                                        "interpolate", ["linear"], ["min", ["heatmap-density"], 0.75],
                                        0, "rgba(0, 128, 0, 0)",
                                        0.1, "#00cc00",
                                        0.25, "#ccff00",
                                        0.4, "#ffff00",
                                        0.55, "#ffcc00",
                                        0.7, "#ff8000",
                                        0.75, "#ff8000"
                                    ],
                                    "heatmap-opacity": 0.8
                                }}
                            />
                        </Source>

                        {/*          <Source id="heatmap-spain-region" type="geojson" data={spain_region}>
                                                    <Layer
                                                        id="heatmap-spain-region"
                                                        type="heatmap"
                                                        beforeId="mask-land-fill"
                                                        paint={{
                                                            "heatmap-weight": ["get", "weight"],
                                                            "heatmap-radius": [
                                                                "interpolate", ["linear"], ["zoom"],
                                                                3, ["*", ["get", "radius"], 0.04],
                                                                4, ["*", ["get", "radius"], 0.05],
                                                                5, ["*", ["get", "radius"], 0.06],
                                                                6, ["*", ["get", "radius"], 0.1],
                                                                7, ["*", ["get", "radius"], 0.16],
                                                                8, ["*", ["get", "radius"], 0.24],
                                                                9, ["*", ["get", "radius"], 0.5],
                                                                10, ["*", ["get", "radius"], 1.0],
                                                                11, ["*", ["get", "radius"], 1.95],
                                                                12, ["*", ["get", "radius"], 3.8],
                                                                13, ["*", ["get", "radius"], 7],
                                                            ],
                                                            "heatmap-intensity": [
                                                                "interpolate", ["linear"], ["zoom"],
                                                                2, 0.003 * intensityMultiplier,
                                                                10, 1.1 * intensityMultiplier,
                                                            ],
                                                           "heatmap-color": [
                                        "interpolate", ["linear"], ["min", ["heatmap-density"], 0.75],
                                        0, "rgba(0, 128, 0, 0)",
                                        0.1, "#00cc00",
                                        0.25, "#ccff00",
                                        0.4, "#ffff00",
                                        0.55, "#ffcc00",
                                        0.7, "#ff8000",
                                        0.75, "#ff8000"
                                    ],
                                                            "heatmap-opacity": 0.8
                                                        }}
                                                    />
                                                </Source> 
                                                */}

                        <Source id="heatmap-north" type="geojson" data={north}>
                            <Layer
                                id="heatmap-north"
                                type="heatmap"
                                beforeId="mask-land-fill"
                                paint={{
                                    "heatmap-weight": ["get", "weight"],
                                    "heatmap-radius": [
                                        "interpolate", ["linear"], ["zoom"],
                                        3, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.04],
                                        4, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.05],
                                        5, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.06],
                                        6, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.1],
                                        7, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.16],
                                        8, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.24],
                                        9, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 0.5],
                                        10, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 1.0],
                                        11, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 1.95],
                                        12, ["*", ["get", "radius"], isSRV ? 0.1 * SRV_RADIUS_BOOST : 3.8],
                                        13, ["*", ["get", "radius"],isSRV ? 0.1 * SRV_RADIUS_BOOST : 7],
                                    ],
                                    "heatmap-intensity": [
                                        "interpolate", ["linear"], ["zoom"],
                                        2, 0.003 * intensityMultiplier,
                                        10, 1.1 * intensityMultiplier,
                                    ],
                                    "heatmap-color": [
                                        "interpolate", ["linear"], ["min", ["heatmap-density"], 0.75],
                                        0, "rgba(0, 128, 0, 0)",
                                        0.1, "#00cc00",
                                        0.25, "#ccff00",
                                        0.4, "#ffff00",
                                        0.55, "#ffcc00",
                                        0.7, "#ff8000",
                                        0.75, "#ff8000"
                                    ],
                                    "heatmap-opacity": 0.8
                                }}
                            />
                        </Source>
                    </>
                )}
                {/*     
                <Source
                    id="ship-noise-heat"
                    type="geojson"
                    data={{
                        type: "FeatureCollection",
                        features: [
                            ...(sjeverni_jadran.features ?? []),
                            ...(juzni_jadran.features ?? []),
                            ...(haifa_jug.features ?? []),
                            ...(haifa_sjever.features ?? [])
                        ]
                    }}
                >
                    <Layer
                        id="ray-dots"
                        type="circle"
                        paint={{
                            "circle-radius": 4,
                            "circle-color": "#ff0000",
                            "circle-opacity": 0.9
                        }}
                    />

                    <Layer
                        id="ray-colored"
                        type="circle"
                        paint={{
                            "circle-radius": 5,
                            "circle-color": [
                                "interpolate",
                                ["linear"],
                                ["get", "ring"],
                                0.25, "red",
                                0.50, "blue"
                            ]

                        }}
                    />
                </Source>
*/}

<div
    style={{
        position: "absolute",
        bottom: "40px",
        left: "10px",
        zIndex: 1200
    }}
>
    <ScaleControl
        position="bottom-left"
        maxWidth={120}
        unit="metric"
    />
</div>

                {showMPA && (
                    <Source
                        id="mpa"
                        type="geojson"
                        data={process.env.PUBLIC_URL + "/mpa/merged_mpa_global_marine.geojson"}
                    >
                        <Layer
                            id="mpa-outline"
                            type="line"
                            paint={{
                                "line-color": "#000000",
                                "line-width": 2,
                                "line-dasharray": [2, 2],
                                "line-opacity": 0.9
                            }}
                        />
                    </Source>
                )}



            </MapGL>
            {showShipModal && selectedShip && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0,0,0,0.55)",
                        backdropFilter: "blur(4px)",
                        zIndex: 99999999,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}
                    onClick={() => setShowShipModal(false)}
                >
                    <div
                        style={{
                            background: "#fff",
                            borderRadius: "18px",
                            boxShadow: "0 10px 40px rgba(0,0,0,0.4)",
                            width: "720px",
                            height: "530px",
                            display: "flex",
                            flexDirection: "column",
                            position: "relative",
                            overflow: "hidden",
                            padding: "28px 32px",
                            animation: "fadeIn 0.25s ease-in-out"
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setShowShipModal(false)}
                            style={{
                                position: "absolute",
                                top: "14px",
                                right: "18px",
                                background: "transparent",
                                border: "none",
                                fontSize: "24px",
                                cursor: "pointer",
                                color: "#666"
                            }}
                        >
                            ✕
                        </button>

                        <h2
                            style={{
                                textAlign: "center",
                                fontSize: "20px",
                                marginBottom: "18px",
                                color: "#0b5394",
                                fontWeight: "bold",
                                borderBottom: "2px solid #dfe6ef",
                                paddingBottom: "6px"
                            }}
                        >
                            Vessel Details
                        </h2>

                        <div
                            style={{
                                flex: 1,
                                overflowY: "auto",
                                display: "grid",
                                gridTemplateColumns: "repeat(2, 1fr)",
                                gap: "12px 20px",
                                paddingRight: "6px"
                            }}
                        >
                            {Object.entries(selectedShip).map(([key, value]) => (
                                <div
                                    key={key}
                                    style={{
                                        background: "#f9fafc",
                                        borderRadius: "10px",
                                        padding: "10px 12px",
                                        boxShadow: "0 1px 2px rgba(0,0,0,0.1)"
                                    }}
                                >
                                    <div
                                        style={{
                                            fontWeight: "bold",
                                            color: "#333",
                                            fontSize: "13px",
                                            marginBottom: "3px"
                                        }}
                                    >
                                        {key.replace(/_/g, " ")}
                                    </div>
                                    <div style={{ fontSize: "13px", color: "#555", wordBreak: "break-all" }}>
                                        {String(value ?? "N/A")}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}




        </div>
    );
};

export default ShipMap;



